import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  MapPin,
  Phone,
  Truck,
  Download,
  Send,
  Eye,
  ChevronLeft,
  ChevronRight,
  Mail,
  AlertTriangle,
  ReceiptText,
} from 'lucide-react';
import { OrderNotesPanel } from '../NotesPanel/NotesPanel';
import {
  useRemoveCarrier,
  useListCarriers,
  useGenerateBOL,
  useSendBOL,
  usePreviewBOL,
  SendBOLResult,
} from '../../features/carriers/api';
import { coiWarningText } from '../../features/carriers/coi';
import { toast } from 'react-toastify';
import {
  useListAddresses,
  useListCustomers,
  useEnableCustomerLocationLoadOption,
  useDisableCustomerLocationLoadOption,
  useSetCustomerLocationProductAccess,
} from '../../features/customers/api';
import { useListProducts } from '../../features/products/api';
import { useListLocations } from '../../features/warehouses/api';
import { SearchableSelect } from '../SearchableSelect/SearchableSelect';
import './OrgOrderRow.scss';
import {
  useSavedOrderTags,
  useSaveOrderTag,
  useDeleteSavedOrderTag,
  useNotifyPickupCustomer,
  useOrgOrderAuditLog,
} from '../../features/org-orders/api';
import { ReplicateOrderDialog } from '../ReplicateOrderDialog/ReplicateOrderDialog';
import {
  formatMoney,
  formatFullAddress,
  isInvalidDollarInput,
  formatPhone,
  limitMoneyInput,
  parseQuantityInput,
} from '../../utils/formatters';
import { isApproveDisabled, isPrimaryActionDisabled, saveDisabledReason } from './saveGate';
import { useEditSession } from '../../hooks/editSession/useEditSession';
import { useDebouncedSearch } from '../../hooks/useDebouncedSearch';
import SearchInput from '../SearchInput/SearchInput';
import { ProductAccessManager } from '../ProductAccessManager/ProductAccessManager';
import { LocationLoadOptionManager } from '../LocationLoadOptionManager/LocationLoadOptionManager';
import { describeDirtyFields } from '../../hooks/editSession/dirtyFields';
import { DateField } from '../DateField/DateField';
import { OrderDocumentsPanel } from '../OrderDocumentsPanel/OrderDocumentsPanel';
import DeliveryTicketCard from '../DeliveryTicketCard/DeliveryTicketCard';
import { BOLPreviewDialog } from '../BOLPreviewDialog/BOLPreviewDialog';
import { blobErrorMessage } from '../../utils/errors';
import CopyButton from '../CopyButton/CopyButton';
import { isoToDisplay } from '../DateField/dateMask';
import {
  useFuelSurcharge,
  pickSurchargeForDate,
  describeSurcharge,
} from '../../features/fuel-surcharge/api';
import { computeOrderPricing, PricingBreakdownView } from '../../features/pricing';
import { dollarsInputToCents } from '../../features/addresses/addressForm/money';
import {
  EMAIL_PATTERN,
  buildRecipientSources,
  dedupeEmails,
  defaultRecipientIds,
  parseEmailList,
} from './bolRecipients';
import { useListLoadOptions } from '../../features/loads/api';
import { lastPickupEmailAt, useLoadNotifications } from '../../features/order-notifications/api';
import { EmailHistoryDialog } from './EmailHistoryDialog';
import { PickupRecipientsDialog } from './PickupRecipientsDialog';
import { InvoiceDialog } from '../InvoiceDialog/InvoiceDialog';

// Form rules — pure, unit-tested, no React.
// See features/org-orders/manageLoadForm.
import {
  CARRIER_DEPENDENT_STATUSES,
  FORM_FIELD_LABELS,
  RESET_FORM,
  STATUS_OPTIONS,
  UNTRACKED_FORM_FIELDS,
  assigningCarrierAdvancesStatus,
  buildInitialState,
  buildSavePayload,
  canAssignCarrierFromStatus,
  formReducer,
  isBOLDirty,
  resolveCarrierRateCents,
} from '../../features/org-orders/manageLoadForm';
import type { FormAction, FormState } from '../../features/org-orders/manageLoadForm';
import fallbackProductImage from '../../assets/defeo-logo.png';
import { NavLink } from 'react-router-dom';

// fmtMoney is an alias kept local for brevity
const fmtMoney = formatMoney;

type ProductLocationAccessState = 'ENABLED' | 'PREVIEW' | 'DISABLED';

interface CustomerLocationCatalogProduct {
  id: string;
  name: string;
  imageUrl?: string | null;
  deliveryType?: string | null;
  updatedAt?: string;
  warehouseIds?: string[];
  palletSizes?: any[];
  access: ProductLocationAccessState;
}

function splitProductsByLocationAccess(products: CustomerLocationCatalogProduct[]) {
  return {
    enabled: products.filter((product) => product.access === 'ENABLED'),
    preview: products.filter((product) => product.access === 'PREVIEW'),
    disabled: products.filter((product) => product.access === 'DISABLED'),
  };
}

function productLocationLabel(address: any): string {
  if (!address) return '';

  return [address.label || address.fullName, address.line1, address.city, address.state]
    .filter(Boolean)
    .join(', ');
}

function formatBlumeMoney(value: number | null | undefined) {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return '—';
  }

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(value);
}

function formatTierDiscount(value: number | null | undefined) {
  const cents = Number(value);

  if (!Number.isFinite(cents) || cents < 0) {
    return '—';
  }

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(cents / 100);
}

function normalizeTierBadgeColor(value: unknown): string {
  return typeof value === 'string' && /^#[0-9a-f]{6}$/i.test(value) ? value : '#64748b';
}

// ─── RejectReasonDialog ───────────────────────────────────────────────────────

function RejectReasonDialog({
  reason,
  onChangeReason,
  onCancel,
  onConfirm,
  isSubmitting,
}: {
  reason: string;
  onChangeReason: (r: string) => void;
  onCancel: () => void;
  onConfirm: () => void;
  isSubmitting?: boolean;
}) {
  return (
    <div className="modal" role="dialog" aria-modal="true" aria-labelledby="reject-title">
      <div className="modal__backdrop" onClick={onCancel} />
      <div className="modal__panel">
        <div className="modal__header">
          <h3 id="reject-title" className="modal__title">
            Reject item
          </h3>
        </div>
        <div className="modal__body">
          <div className="form-control">
            <label htmlFor="reject-reason">Reason (optional)</label>
            <textarea
              id="reject-reason"
              className="input-lg"
              placeholder="Briefly explain why this item is rejected…"
              value={reason}
              onChange={(e) => onChangeReason(e.target.value)}
            />
            <small className="muted">This note will be visible to the customer on the order.</small>
          </div>
        </div>
        <div className="modal__footer">
          <button className="btn btn--ghost" onClick={onCancel} disabled={isSubmitting}>
            Cancel
          </button>
          <button className="btn btn--danger" onClick={onConfirm} disabled={isSubmitting}>
            {isSubmitting ? 'Rejecting…' : 'Reject item'}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Turns a send result into the right toast. Recipients are sent separately, so
 * "it worked" and "it failed" aren't the only two outcomes — a partial send has
 * to name who missed out, or the admin has no way to know who to chase.
 */
export function reportBOLSendResult(result: SendBOLResult | undefined) {
  const sent = result?.sent ?? [];
  const failed = result?.failed ?? [];

  if (failed.length) {
    toast.warn(
      `BOL sent to ${sent.length} of ${sent.length + failed.length} recipients. ` +
        `Failed: ${failed.map((entry) => entry.email).join(', ')}`,
      { autoClose: false }
    );
    return;
  }

  toast.success(
    sent.length > 1 ? `BOL sent to ${sent.length} recipients` : 'BOL sent successfully'
  );
}

// ─── SendBOLDialog ────────────────────────────────────────────────────────────

export function SendBOLDialog({
  item,
  carrier,
  contacts,
  assignedContactId,
  hasUnsavedChanges,
  onCancel,
  onConfirm,
  isSubmitting,
}: {
  item: any;
  carrier: any;
  /** The carrier's contact cards. Each one with an email becomes a checkbox. */
  contacts?: any[];
  assignedContactId?: string | null;
  hasUnsavedChanges?: boolean;
  onCancel: () => void;
  onConfirm: (emails: string[], note?: string, contactName?: string) => void;
  isSubmitting?: boolean;
}) {
  const sources = useMemo(
    () => buildRecipientSources(contacts, carrier?.emails, assignedContactId),
    [contacts, carrier?.emails, assignedContactId]
  );

  // Only the contact assigned to this load starts checked, so the default send
  // matches what the single-recipient field used to do. Everyone else is one
  // tick away.
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [extraEmails, setExtraEmails] = useState('');
  const [note, setNote] = useState('');

  useEffect(() => {
    setSelectedIds(defaultRecipientIds(sources));
  }, [sources]);

  const toggle = (id: string) =>
    setSelectedIds((current) =>
      current.includes(id) ? current.filter((entry) => entry !== id) : [...current, id]
    );

  const allSelected = sources.length > 0 && selectedIds.length === sources.length;

  const extras = useMemo(() => parseEmailList(extraEmails), [extraEmails]);
  const invalidExtras = useMemo(
    () => extras.filter((entry) => !EMAIL_PATTERN.test(entry)),
    [extras]
  );

  const selectedSources = sources.filter((source) => selectedIds.includes(source.id));

  // Deduped the same way the server does, so the count on the button is the
  // number of emails that will actually be sent.
  const recipients = useMemo(
    () => dedupeEmails([...selectedSources.map((source) => source.email), ...extras]),
    [selectedSources, extras]
  );

  const canSend = recipients.length > 0 && invalidExtras.length === 0 && !isSubmitting;

  const handleSend = () => {
    if (!canSend) return;

    const names = selectedSources
      .map((source) => source.name)
      .filter((name): name is string => Boolean(name && name.trim()));

    onConfirm(recipients, note.trim() || undefined, names.join(', ') || undefined);
  };

  return (
    <div className="modal" role="dialog" aria-modal="true">
      <div className="modal__backdrop" onClick={onCancel} />
      <div className="modal__panel">
        <div className="modal__header">
          <h3 className="modal__title">Send BOL</h3>
          <div className="muted tiny bol-carrier-subtitle">
            {carrier ? `To: ${carrier.companyName}` : 'No carrier assigned'}
          </div>
        </div>

        <div className="modal__body">
          {hasUnsavedChanges && (
            <div className="info-banner info-banner--warning error-box--spaced">
              You have unsaved changes. The BOL is built from what is already saved.
            </div>
          )}

          <div className="form-control">
            <div className="bol-recipients__heading">
              <label>Email address *</label>

              {sources.length > 1 && (
                <button
                  type="button"
                  className="btn btn--link bol-recipients__select-all"
                  onClick={() =>
                    setSelectedIds(allSelected ? [] : sources.map((source) => source.id))
                  }
                >
                  {allSelected ? 'Clear all' : 'Select all'}
                </button>
              )}
            </div>

            {sources.length > 0 ? (
              <ul className="bol-recipients">
                {sources.map((source) => (
                  <li key={source.id} className="bol-recipient">
                    <label className="bol-recipient__label">
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(source.id)}
                        onChange={() => toggle(source.id)}
                      />
                      <span className="bol-recipient__body">
                        <span className="bol-recipient__name">
                          {source.name || source.email}
                          {source.type && (
                            <span className="bol-recipient__type">
                              {source.type === 'DISPATCHER' ? 'Dispatcher' : 'Driver'}
                            </span>
                          )}
                          {source.assigned && (
                            <span className="bol-recipient__type bol-recipient__type--assigned">
                              Assigned
                            </span>
                          )}
                        </span>
                        {source.name && (
                          <span className="bol-recipient__email">{source.email}</span>
                        )}
                      </span>
                    </label>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="muted tiny bol-recipients__empty">
                This carrier has no contacts or emails on file. Add addresses below.
              </div>
            )}

            <input
              id="bol-email"
              type="text"
              className="bol-extra-emails"
              placeholder="someone@example.com, someone.else@example.com"
              value={extraEmails}
              onChange={(e) => setExtraEmails(e.target.value)}
              aria-describedby="bol-email-help"
              aria-invalid={invalidExtras.length > 0}
            />
            <div id="bol-email-help" className="muted tiny bol-extra-emails__help">
              Separate each email address with a comma.
            </div>

            {invalidExtras.length > 0 && (
              <div className="error-box error-box--spaced">
                Check {invalidExtras.length === 1 ? 'this address' : 'these addresses'}:{' '}
                {invalidExtras.join(', ')}
              </div>
            )}
          </div>

          <div className="form-control">
            <label htmlFor="bol-note">Note (optional)</label>
            <textarea
              id="bol-note"
              className="select-lg"
              placeholder="Additional message to include with BOL…"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
            />
          </div>

          <div className="muted tiny bol-recipients__summary">
            {recipients.length === 0
              ? 'Select at least one recipient.'
              : `Each recipient gets their own copy — ${recipients.length} ${
                  recipients.length === 1 ? 'email' : 'emails'
                } will be sent.`}
          </div>
        </div>

        <div className="modal__footer">
          <div className="actions bol-modal-actions">
            <button className="btn btn--primary" onClick={handleSend} disabled={!canSend}>
              {isSubmitting
                ? 'Sending…'
                : recipients.length > 1
                  ? `Send BOL to ${recipients.length} recipients`
                  : 'Send BOL'}
            </button>
            <button className="btn btn--ghost" onClick={onCancel} disabled={isSubmitting}>
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

interface CarrierRateMapping {
  warehouseId: string;
  carrierRateCents: number;
}

interface DeliveryAddressLoadOption {
  id: string;
  orgLoadOptionId?: string;
  name: string;
  capacityLabel?: string | null;
  enableCarrierRate?: boolean;
  active?: boolean;
}

interface DeliveryAddressOption {
  id: string;
  fullName?: string | null;
  phone?: string | null;
  line1?: string | null;
  line2?: string | null;
  city?: string | null;
  state?: string | null;
  postalCode?: string | null;
  country?: string | null;
  carrierRates?: CarrierRateMapping[];
  loadOptions?: DeliveryAddressLoadOption[];
  products?: Array<{
    productId: string;
    isEnabled: boolean;
    isPreview: boolean;
  }>;
  defaultFulfillmentMethodId?: string | null;
  requiresPoNumber?: boolean;
}

interface AddressSectionProps {
  form: FormState;
  dispatch: React.Dispatch<FormAction>;
  item: any;

  locationOptions: Array<{
    id: string;
    name: string;
    line1?: string;
  }>;

  overrideLocationOptions: Array<{
    id: string;
    name: string;
    line1?: string;
  }>;

  selectedLocation: any;
  deliveryAddresses: DeliveryAddressOption[];
  selectedDeliveryAddress: DeliveryAddressOption | null;
  canEditAttributes: boolean;

  organizationDefaultLoadOption?: any | null;
  /** Anchors the Ship To field so a customer switch can scroll it into view. */
  shipToRef?: React.RefObject<HTMLDivElement | null>;
}

function AddressSection({
  form,
  dispatch,
  item,
  locationOptions,
  overrideLocationOptions,
  selectedLocation,
  deliveryAddresses,
  selectedDeliveryAddress,
  canEditAttributes,
  organizationDefaultLoadOption,
  shipToRef,
}: AddressSectionProps) {
  const shipFromName = selectedLocation?.name ?? null;
  const shipFromAddr = formatFullAddress(selectedLocation);
  const shipTo = formatFullAddress(selectedDeliveryAddress);

  return (
    <div className="section">
      <div className="section-label">
        <MapPin size={12} />
        Ship From
      </div>

      {canEditAttributes && (locationOptions.length > 0 || overrideLocationOptions.length > 0) && (
        <div className="form-control" style={{ marginBottom: 8 }}>
          <select
            className="select-lg"
            value={form.locationId}
            onChange={(e) => {
              const newLocationId = e.target.value;

              const carrierRateCents = resolveCarrierRateCents(
                selectedDeliveryAddress,
                newLocationId
              );

              dispatch({
                type: 'SET_LOCATION',
                locationId: newLocationId,
                carrierRateCents,
              });
            }}
          >
            <option value="">Select warehouse…</option>

            {/* Keep the current selection visible even if it is archived and so
                in neither group below — otherwise the select would blank out. */}
            {form.locationId &&
              !locationOptions.some((l) => l.id === form.locationId) &&
              !overrideLocationOptions.some((l) => l.id === form.locationId) &&
              selectedLocation && (
                <option value={form.locationId}>
                  {selectedLocation.name || selectedLocation.line1 || form.locationId}
                </option>
              )}

            {locationOptions.length > 0 && (
              <optgroup label="Assigned to customer">
                {locationOptions.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.name || l.line1 || l.id}
                  </option>
                ))}
              </optgroup>
            )}

            {overrideLocationOptions.length > 0 && (
              <optgroup label="Other active warehouses (override)">
                {overrideLocationOptions.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.name || l.line1 || l.id}
                  </option>
                ))}
              </optgroup>
            )}
          </select>

          {form.locationId && overrideLocationOptions.some((l) => l.id === form.locationId) && (
            <div style={{ fontSize: 12, color: '#b45309', marginTop: 6 }}>
              Override — this warehouse isn’t assigned to the customer. One-off for this load only;
              it doesn’t change their assignments.
            </div>
          )}
        </div>
      )}

      {selectedLocation ? (
        <div className="ship-card" style={{ marginBottom: 10 }}>
          <div className="ship-line">
            <MapPin size={14} className="icon" />
            <span>{shipFromName || '—'}</span>
          </div>
          {shipFromAddr && <div className="ship-line text">{shipFromAddr}</div>}
        </div>
      ) : (
        <p className="muted section-muted">Not assigned yet.</p>
      )}

      <div className="section-label section-label--mt" ref={shipToRef}>
        <MapPin size={12} />
        Ship To
      </div>

      {canEditAttributes && deliveryAddresses.length > 0 && (
        <div className="form-control" style={{ marginBottom: 8 }}>
          <select
            className="select-lg"
            value={form.deliveryAddressId}
            onChange={(e) => {
              const newAddressId = e.target.value;

              const address = deliveryAddresses.find((candidate) => candidate.id === newAddressId);

              const carrierRateCents = resolveCarrierRateCents(address, form.locationId);

              const locationLoadOptions = (address?.loadOptions ?? []).filter(
                (option) => option.active !== false
              );

              /*
               * Explicit location configuration wins.
               * Organization default is only a fallback
               * when the location has zero methods.
               */
              const effectiveLoadOptions =
                locationLoadOptions.length > 0
                  ? locationLoadOptions
                  : organizationDefaultLoadOption
                    ? [
                        {
                          ...organizationDefaultLoadOption,

                          id: organizationDefaultLoadOption.id,

                          orgLoadOptionId: organizationDefaultLoadOption.id,

                          isOrganizationDefault: true,
                        },
                      ]
                    : [];

              const currentMethodStillAvailable = effectiveLoadOptions.some(
                (option) => (option.orgLoadOptionId ?? option.id) === form.loadOptionId
              );

              const defaultMethodId =
                locationLoadOptions.length > 0
                  ? (address?.defaultFulfillmentMethodId ?? '')
                  : (organizationDefaultLoadOption?.id ?? '');

              const defaultMethodIsAvailable =
                Boolean(defaultMethodId) &&
                effectiveLoadOptions.some(
                  (option) => (option.orgLoadOptionId ?? option.id) === defaultMethodId
                );

              const nextLoadOptionId = currentMethodStillAvailable
                ? form.loadOptionId
                : defaultMethodIsAvailable
                  ? defaultMethodId
                  : (effectiveLoadOptions[0]?.orgLoadOptionId ?? effectiveLoadOptions[0]?.id ?? '');

              dispatch({
                type: 'SET_DELIVERY_ADDRESS',
                addressId: newAddressId,
                carrierRateCents,
                loadOptionId: nextLoadOptionId,
              });
            }}
          >
            {/* Placeholder so a just-switched customer starts with no Ship To
                selected, rather than the browser defaulting to the first option. */}
            {!form.deliveryAddressId && <option value="">Select a delivery location…</option>}
            {deliveryAddresses.map((a: any) => (
              <option key={a.id} value={a.id}>
                {[a.fullName, a.line1, a.city, a.state].filter(Boolean).join(', ')}
              </option>
            ))}
          </select>
        </div>
      )}

      {selectedDeliveryAddress ? (
        <div className="ship-card" style={{ marginBottom: 10 }}>
          <div className="ship-line ship-card__name-row">
            <MapPin size={14} className="icon" />
            <span>{selectedDeliveryAddress.fullName || '—'}</span>
            <CopyButton value={shipTo} label="Ship to address" className="ship-card__copy" />
          </div>
          {shipTo && <div className="ship-line text">{shipTo}</div>}
          {selectedDeliveryAddress.phone && (
            <div className="ship-line">
              <Phone size={12} className="icon" />
              <span>{formatPhone(selectedDeliveryAddress.phone)}</span>
            </div>
          )}
        </div>
      ) : (
        <p className="muted section-muted">No delivery address on file.</p>
      )}

      <div className="order-summary-row">
        <span className="muted tiny">
          Qty: <strong>{item.quantity}</strong>
        </span>
        <span className="muted tiny">
          Total: <strong>{fmtMoney(item.lineTotal)}</strong>
        </span>
      </div>
    </div>
  );
}

// ─── CarrierSection ───────────────────────────────────────────────────────────

interface CarrierSectionProps {
  form: FormState;
  dispatch: React.Dispatch<FormAction>;
  item: any;
  allCarriers: any[];
  fullCarrierProfile: any;
  canAssignCarrier: boolean;
  /** Whether picking a carrier should also move the load to ASSIGNED. */
  carrierAssignmentAdvancesStatus: boolean;
  canEditAttributes: boolean;
  hasBOLUnsaved: boolean;
  onDownloadBOL: () => void;
  onOpenSendBOL: () => void;
  onPreviewBOL: () => void;
  previewBOLPending: boolean;
  generateBOLPending: boolean;
  removeCarrierPending: boolean;
}

function CarrierSection({
  form,
  dispatch,
  item,
  allCarriers,
  fullCarrierProfile,
  canAssignCarrier,
  carrierAssignmentAdvancesStatus,
  canEditAttributes,
  hasBOLUnsaved,
  onDownloadBOL,
  onOpenSendBOL,
  onPreviewBOL,
  generateBOLPending,
  previewBOLPending,
  removeCarrierPending,
}: CarrierSectionProps) {
  const [isPickingCarrier, setIsPickingCarrier] = useState(false);
  const [carrierSearch, setCarrierSearch] = useState('');

  const filteredCarriers = useMemo(() => {
    // Trim before the empty check, so a search of only spaces shows everything
    // rather than filtering everything out.
    const q = carrierSearch.trim().toLowerCase();
    if (!q) return allCarriers;
    return allCarriers.filter(
      (c) =>
        c.companyName.toLowerCase().includes(q) ||
        c.phone?.toLowerCase().includes(q) ||
        c.dotNumber?.toLowerCase().includes(q) ||
        c.mcNumber?.toLowerCase().includes(q)
    );
  }, [allCarriers, carrierSearch]);

  // COI warning for the currently-assigned carrier (looked up from the list, which
  // carries coiOnFile/coiExpiresAt), shown as a persistent banner on the card.
  const assignedCarrierRow = form.localCarrier
    ? allCarriers.find((x) => x.id === form.localCarrier.id)
    : null;
  const assignedCarrierCoiWarning = assignedCarrierRow
    ? coiWarningText(assignedCarrierRow.coiOnFile, assignedCarrierRow.coiExpiresAt)
    : null;

  const handleRemove = useCallback(() => {
    if (!window.confirm('Remove carrier assignment from this order?')) return;
    const revertsToApproved = CARRIER_DEPENDENT_STATUSES.includes(form.nextStatus);
    dispatch({ type: 'REMOVE_CARRIER', prevStatus: form.nextStatus });
    toast.success(
      revertsToApproved
        ? 'Carrier removed and status set back to Approved. Click Update to save.'
        : 'Carrier removed. Click Update to save.'
    );
  }, [dispatch, form.nextStatus]);

  return (
    <div>
      <div className="section-divider" />
      <div className="section-label">
        <Truck size={12} />
        Carrier
      </div>

      {isPickingCarrier ? (
        <div>
          <div className="form-control carrier-picker-search">
            <SearchInput
              autoFocus
              placeholder="Search by name, phone, DOT/MC…"
              value={carrierSearch}
              onValueChange={setCarrierSearch}
            />
          </div>
          <div className="carrier-picker-list">
            {filteredCarriers.length === 0 && (
              <div className="muted carrier-picker-empty">No carriers found.</div>
            )}
            {filteredCarriers.map((c) => (
              <button
                key={c.id}
                type="button"
                className={`carrier-picker-item${c.id === form.localCarrier?.id ? ' carrier-picker-item--selected' : ''}`}
                onClick={() => {
                  dispatch({
                    type: 'ASSIGN_CARRIER',
                    carrier: c,
                    advanceToAssigned: carrierAssignmentAdvancesStatus,
                  });
                  // Notify but don't block — an expired/missing COI still assigns.
                  const warning = coiWarningText(c.coiOnFile, c.coiExpiresAt);
                  if (warning) {
                    toast.warn(`${warning} You can still assign this carrier.`, {
                      autoClose: 8000,
                    });
                  }
                  setIsPickingCarrier(false);
                  setCarrierSearch('');
                }}
              >
                <div className="carrier-picker-item__name">{c.companyName}</div>
                <div className="carrier-picker-item__meta">
                  {formatPhone(c.phone)}
                  {c.dotNumber ? ` · DOT: ${c.dotNumber}` : ''}
                  {c.mcNumber ? ` · MC: ${c.mcNumber}` : ''}
                </div>
              </button>
            ))}
          </div>
          <button
            type="button"
            className="btn btn--ghost btn--sm carrier-picker-cancel"
            onClick={() => {
              setIsPickingCarrier(false);
              setCarrierSearch('');
            }}
          >
            Cancel
          </button>
        </div>
      ) : form.localCarrier ? (
        <div className="carrier-card">
          {assignedCarrierCoiWarning && (
            <div className="carrier-coi-warning">
              <AlertTriangle size={14} />
              <span>{assignedCarrierCoiWarning}</span>
            </div>
          )}
          <div className="carrier-card__header">
            <a
              className="carrier-card__name carrier-name-link"
              href={`/carriers?carrierId=${form.localCarrier.id}`}
              target="_blank"
              rel="noopener noreferrer"
              title="Open carrier in a new tab"
            >
              {form.localCarrier.companyName}
            </a>
            <div className="carrier-card__header-actions">
              <CopyButton value={form.localCarrier.companyName} label="Carrier name" />
              <button
                className="btn btn--ghost btn--sm"
                onClick={() => {
                  setIsPickingCarrier(true);
                  setCarrierSearch('');
                }}
                disabled={!canAssignCarrier}
              >
                Change
              </button>
              <button
                className="btn btn--ghost btn--sm"
                onClick={handleRemove}
                disabled={!canAssignCarrier || removeCarrierPending}
              >
                {removeCarrierPending ? 'Removing…' : 'Remove'}
              </button>
            </div>
          </div>

          {form.localCarrier.phone && (
            <div className="carrier-card__detail">
              <Phone size={12} className="icon" />
              <span>{formatPhone(form.localCarrier.phone)}</span>
            </div>
          )}
          {form.localCarrier.dotNumber && (
            <div className="carrier-card__detail">
              <span className="label">DOT:</span>
              <span>{form.localCarrier.dotNumber}</span>
            </div>
          )}
          {form.localCarrier.mcNumber && (
            <div className="carrier-card__detail">
              <span className="label">MC:</span>
              <span>{form.localCarrier.mcNumber}</span>
            </div>
          )}

          <div className="form-grid-2col carrier-driver-grid">
            <div className="form-control">
              <label>Driver / Dispatcher</label>
              <select
                className="select-lg"
                value={form.driverContactId ?? ''}
                onChange={(e) => {
                  const contactId = e.target.value || null;
                  const contact = fullCarrierProfile?.contacts?.find(
                    (c: any) => c.id === contactId
                  );
                  dispatch({
                    type: 'SET_DRIVER_CONTACT',
                    contactId,
                    driverName: contact?.name ?? '',
                  });
                }}
                disabled={!canEditAttributes}
              >
                <option value="">None</option>
                {(fullCarrierProfile?.contacts ?? []).map((c: any) => (
                  <option key={c.id} value={c.id}>
                    {c.name} ({c.type === 'DISPATCHER' ? 'Dispatcher' : 'Driver'})
                  </option>
                ))}
              </select>
              {form.driverName && (
                <div className="muted tiny" style={{ marginTop: 4 }}>
                  Assigned: <strong>{form.driverName}</strong>
                </div>
              )}
            </div>
            <div className="form-control">
              <label>Truck #</label>
              <select
                className="select-lg"
                value={form.truckNumber}
                onChange={(e) => dispatch({ type: 'SET_TRUCK', value: e.target.value })}
                disabled={!canEditAttributes}
              >
                <option value="">None</option>
                {[
                  ...new Set([
                    ...(fullCarrierProfile?.truckNumbers ?? []),
                    ...(form.truckNumber ? [form.truckNumber] : []),
                  ]),
                ].map((t: string) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {item.bolSentAt && (
            <div className="bol-sent-badge">
              <span className="bol-sent-badge__icon">✓</span>
              <div className="bol-sent-badge__body">
                <div className="bol-sent-badge__title">BOL sent</div>
                <div className="bol-sent-badge__detail">
                  {new Date(item.bolSentAt).toLocaleString('en-US', {
                    timeZone: 'America/New_York',
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit',
                  })}
                  {item.bolSentToName && ` · ${item.bolSentToName}`}
                  {item.bolSentToEmail && (
                    <span className="bol-sent-badge__email">{item.bolSentToEmail}</span>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="carrier-card__actions carrier-bol-actions">
            <button
              className="btn btn--secondary btn--sm"
              onClick={onPreviewBOL}
              disabled={!canAssignCarrier || previewBOLPending}
            >
              <Eye size={14} />
              {previewBOLPending ? 'Loading…' : 'Preview BOL'}
            </button>
            <button
              className="btn btn--secondary btn--sm"
              onClick={onDownloadBOL}
              disabled={!canAssignCarrier || generateBOLPending}
            >
              <Download size={14} />
              {generateBOLPending ? 'Generating…' : 'Download BOL'}
            </button>
            <button
              className="btn btn--secondary btn--sm"
              onClick={onOpenSendBOL}
              disabled={!canAssignCarrier}
            >
              <Send size={14} />
              Send BOL
            </button>
          </div>
        </div>
      ) : (
        <div className="carrier-empty">
          <div className="carrier-empty__text">
            <Truck size={20} className="carrier-empty__icon" />
            <span>No carrier assigned</span>
          </div>
          <button
            className="btn btn--primary btn--sm"
            onClick={() => {
              setIsPickingCarrier(true);
              setCarrierSearch('');
            }}
            disabled={!canAssignCarrier}
          >
            Assign Carrier
          </button>
          {!canAssignCarrier && (
            <div className="muted tiny carrier-empty__hint">
              Approve this load before assigning a carrier
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── FulfillmentSection ───────────────────────────────────────────────────────

interface FulfillmentSectionProps {
  form: FormState;
  dispatch: React.Dispatch<FormAction>;
  item: any;
  palletSizes: any[];
  isPallet: boolean;
  isWaitlist: boolean;
  hidePrice: boolean;
  isApprovedAlready: boolean;
  canEdit: boolean;
  canEditAttributes: boolean;
  availableLoadOptions: any[];
  /** False until the methods behind `availableLoadOptions` have actually loaded. */
  loadOptionsReady: boolean;
  defaultFulfillmentMethodId?: string | null;
  usingOrganizationDefault?: boolean;
  organizationLoadOptions: any[];
  hasLocationSpecificLoadOptions: boolean;
  onEnableLocationLoadOption: (loadOptionId: string) => Promise<void>;
  onDisableLocationLoadOption: (loadOptionId: string) => Promise<void>;
  locationLoadOptionChangePending?: boolean;
  /** Customer location × warehouse carrier rate, used when re-enabling it. */
  configuredCarrierRateCents: number;
}

function FulfillmentSection({
  form,
  dispatch,
  item,
  palletSizes,
  isPallet,
  isWaitlist,
  hidePrice,
  isApprovedAlready,
  canEdit,
  canEditAttributes,
  availableLoadOptions,
  loadOptionsReady,
  defaultFulfillmentMethodId,
  usingOrganizationDefault,
  organizationLoadOptions,
  hasLocationSpecificLoadOptions,
  onEnableLocationLoadOption,
  onDisableLocationLoadOption,
  locationLoadOptionChangePending = false,
  configuredCarrierRateCents,
}: FulfillmentSectionProps) {
  const [showLocationLoadOptionManager, setShowLocationLoadOptionManager] = useState(false);

  // Fuel surcharge for the delivery week, using the website's EIA feed. Shown
  // read-only below the delivery date; this dialog is org-staff-only.
  const { data: fuelSurchargeWeeks } = useFuelSurcharge();
  const fuelSurcharge = useMemo(
    () =>
      form.deliveryDate ? pickSurchargeForDate(fuelSurchargeWeeks ?? [], form.deliveryDate) : null,
    [fuelSurchargeWeeks, form.deliveryDate]
  );

  // Live pricing estimate for the load (admin-only unless the customer is opted
  // in). The server hands us the per-load facts on `item.pricing`; we recompute
  // as the admin edits quantity / warehouse / delivery date / carrier rate.
  // The selected fulfilment method decides whether a carrier (delivery) rate
  // applies at all; the server stores no delivery fee when it is disabled.
  const carrierRateEnabled =
    availableLoadOptions.find(
      (option: any) => (option.orgLoadOptionId ?? option.id) === form.loadOptionId
    )?.enableCarrierRate !== false;

  const pricingInputs = (item as any).pricing;
  const pricingBreakdown = useMemo(() => {
    if (!pricingInputs?.enabled) return null;
    const weeks = fuelSurchargeWeeks ?? [];
    const estimateWeek =
      fuelSurcharge?.week ?? weeks.find((w) => w.isCurrent) ?? weeks[weeks.length - 1] ?? null;
    const deliveryRateCents = carrierRateEnabled ? dollarsInputToCents(form.deliveryFeeInput) : 0;
    return computeOrderPricing({
      region: pricingInputs.region,
      tons: Number(form.quantity) || 0,
      deliveredPriceCents: pricingInputs.deliveredPriceCents,
      stonePriceCents: pricingInputs.stonePriceByLocation?.[form.locationId] ?? null,
      deliveryRateCents,
      isPickup: deliveryRateCents === 0,
      tierDiscountCents: pricingInputs.tierDiscountCents,
      applyTierDiscount: pricingInputs.applyTierDiscount,
      directShipFeeCents: dollarsInputToCents(form.carrierRateInput),
      materialPct: estimateWeek?.materialPct ?? 0,
      deliveryPct: estimateWeek?.deliveryPct ?? 0,
    });
  }, [
    pricingInputs,
    fuelSurcharge,
    fuelSurchargeWeeks,
    form.quantity,
    form.locationId,
    form.deliveryFeeInput,
    form.carrierRateInput,
    carrierRateEnabled,
  ]);

  const activeOrganizationLoadOptions = useMemo(
    () =>
      (Array.isArray(organizationLoadOptions) ? organizationLoadOptions : [])
        .filter((option: any) => option.active !== false && !option.deletedAt)
        .sort((a: any, b: any) => String(a.name ?? '').localeCompare(String(b.name ?? ''))),
    [organizationLoadOptions]
  );

  const explicitlyEnabledLoadOptionIds = useMemo(
    () =>
      new Set(
        availableLoadOptions
          .filter((option: any) => !option.isOrganizationDefault)
          .map((option: any) => option.orgLoadOptionId ?? option.id)
      ),
    [availableLoadOptions]
  );

  const priceOverrideInvalid = isInvalidDollarInput(form.priceOverrideInput);
  const deliveryFeeInvalid = isInvalidDollarInput(form.deliveryFeeInput);
  const carrierRateInvalid = isInvalidDollarInput(form.carrierRateInput);
  const canEditSchedule = canEdit && !!form.locationId && isApprovedAlready;

  return (
    <div className="section">
      {!item.product?.deletedAt && (
        <div className="form-grid-2col">
          <div className="form-group-header">Fulfillment</div>

          <div className="form-control" style={{ gridColumn: '1 / -1' }}>
            <div
              className="flex-between"
              style={{ alignItems: 'baseline', gap: 8, marginBottom: 4 }}
            >
              <label>Fulfilment Method</label>

              {canEditAttributes && form.deliveryAddressId && (
                <button
                  type="button"
                  className="btn btn--link"
                  onClick={() => setShowLocationLoadOptionManager((current) => !current)}
                  disabled={!loadOptionsReady || locationLoadOptionChangePending}
                >
                  {showLocationLoadOptionManager ? 'Done' : 'Manage methods'}
                </button>
              )}
            </div>

            <select
              className="select-lg"
              value={form.loadOptionId}
              onChange={(e) => {
                const next = availableLoadOptions.find(
                  (option: any) => (option.orgLoadOptionId ?? option.id) === e.target.value
                );
                // Switching from a method without a carrier rate to one with it:
                // re-pull the customer location's rate (the saved fee is 0).
                const restoreCarrierRate = !carrierRateEnabled && next?.enableCarrierRate !== false;
                dispatch({
                  type: 'SET_LOAD_OPTION',
                  value: e.target.value,
                  ...(restoreCarrierRate ? { carrierRateCents: configuredCarrierRateCents } : {}),
                });
              }}
              disabled={
                !canEditAttributes ||
                !form.deliveryAddressId ||
                !loadOptionsReady ||
                availableLoadOptions.length === 0
              }
            >
              {!form.deliveryAddressId && <option value="">Select a delivery address first</option>}

              {form.deliveryAddressId && (
                <>
                  <option value="">
                    {!loadOptionsReady
                      ? 'Loading fulfilment methods…'
                      : availableLoadOptions.length > 0
                        ? 'Select fulfilment method…'
                        : 'No fulfilment methods configured for this location'}
                  </option>

                  {availableLoadOptions.map((option: any) => {
                    const optionId = option.orgLoadOptionId ?? option.id;
                    const isDefault = optionId === defaultFulfillmentMethodId;

                    return (
                      <option key={optionId} value={optionId}>
                        {option.capacityLabel
                          ? `${option.name} - ${option.capacityLabel}t`
                          : option.name}
                        {isDefault
                          ? usingOrganizationDefault
                            ? ' • Organization default'
                            : ' • Default'
                          : ''}
                      </option>
                    );
                  })}
                </>
              )}
            </select>

            {form.deliveryAddressId && loadOptionsReady && usingOrganizationDefault && (
              <div className="muted tiny" style={{ marginTop: 6 }}>
                No fulfilment methods are configured specifically for this delivery location. The
                organization default is being used.
              </div>
            )}

            {showLocationLoadOptionManager && form.deliveryAddressId && loadOptionsReady && (
              <LocationLoadOptionManager
                options={activeOrganizationLoadOptions}
                explicitlyEnabledIds={explicitlyEnabledLoadOptionIds}
                hasLocationSpecificLoadOptions={hasLocationSpecificLoadOptions}
                defaultFulfillmentMethodId={defaultFulfillmentMethodId ?? null}
                onEnable={onEnableLocationLoadOption}
                onDisable={onDisableLocationLoadOption}
                pending={locationLoadOptionChangePending}
              />
            )}
          </div>

          {isPallet && (
            <div className="form-control" style={{ gridColumn: '1 / -1' }}>
              <label>Pallet size</label>
              <select
                className="select-lg"
                value={form.palletSizeId}
                onChange={(e) => dispatch({ type: 'SET_PALLET_SIZE', value: e.target.value })}
                disabled={!canEditAttributes}
              >
                <option value="" disabled>
                  Select pallet size…
                </option>
                {palletSizes.map((p) => (
                  <option key={p.id} value={p.id}>
                    {`${p.template.name} - ${p.template.type}`}
                    {` - ${p.template.lengthIn}×${p.template.widthIn}×${p.template.heightIn} in`}
                    {p.template.weightTons ? ` • ${p.template.weightTons}t` : ''}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="form-control">
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <CopyButton
                value={Number.isFinite(form.quantity) ? String(form.quantity) : ''}
                label="Quantity"
              />
              <label>Quantity (t)</label>
            </div>
            <input
              className="select-lg"
              type="number"
              min="0.01"
              step="0.01"
              value={form.quantity}
              onChange={(e) =>
                dispatch({ type: 'SET_QUANTITY', value: parseQuantityInput(e.target.value) })
              }
              disabled={!canEditAttributes}
            />
          </div>

          <div className="form-control">
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <CopyButton value={form.ticketNumber} label="Ticket number" />
              <label>Ticket Number (optional)</label>
            </div>
            <input
              className="select-lg"
              type="text"
              value={form.ticketNumber}
              onChange={(e) => dispatch({ type: 'SET_TICKET_NUMBER', value: e.target.value })}
              disabled={!canEditAttributes}
            />
          </div>

          <div className="form-group-header">Pricing</div>

          {!hidePrice && (
            <div className="form-control">
              <label>Override price (optional)</label>
              <input
                className="select-lg"
                type="number"
                min="0"
                step="1"
                placeholder="$ per load"
                value={form.priceOverrideInput}
                onChange={(e) => dispatch({ type: 'SET_PRICE_OVERRIDE', value: e.target.value })}
                disabled={!canEditAttributes || !form.locationId}
              />
              {priceOverrideInvalid && (
                <div className="error-text">Enter a valid non-negative number or leave blank.</div>
              )}
            </div>
          )}

          <div className="form-control">
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <CopyButton value={form.deliveryFeeInput} label="Carrier rate" />
              <label>Carrier rate (optional)</label>
            </div>
            <input
              className="select-lg"
              type="number"
              min="0"
              step="0.01"
              placeholder="$ per order"
              // `deliveryFeeInput` is dollars everywhere now — this input used
              // to divide on the way in and multiply on the way out, while two
              // reducer cases wrote cents into the same field.
              value={carrierRateEnabled ? form.deliveryFeeInput : ''}
              onChange={(e) => {
                dispatch({
                  type: 'SET_DELIVERY_FEE',
                  value: e.target.value === '' ? '' : limitMoneyInput(e.target.value),
                });
              }}
              disabled={!canEditAttributes || !carrierRateEnabled}
            />
            {!carrierRateEnabled && (
              <div className="muted tiny" style={{ marginTop: 4 }}>
                Carrier rate is disabled for this fulfilment method.
              </div>
            )}
            {carrierRateEnabled && deliveryFeeInvalid && (
              <div className="error-text">Enter a valid non-negative number or leave blank.</div>
            )}
          </div>

          <div className="form-control">
            <label>Direct Ship Fee (optional)</label>
            <input
              className="select-lg"
              type="number"
              min="0"
              step="0.01"
              placeholder="$ direct ship fee"
              value={form.carrierRateInput}
              onChange={(e) =>
                dispatch({ type: 'SET_CARRIER_RATE', value: limitMoneyInput(e.target.value) })
              }
              disabled={!canEdit || !form.locationId || !isApprovedAlready}
            />
            {carrierRateInvalid && (
              <div className="error-text">Enter a valid non-negative number or leave blank.</div>
            )}
          </div>

          <div className="form-group-header">Schedule</div>

          <div className="form-control">
            <label>Pickup Date{form.nextStatus === 'READY_FOR_PICKUP' ? ' *' : ''}</label>
            <DateField
              className="select-lg"
              clearable
              value={form.pickupDate}
              onChange={(value) => dispatch({ type: 'SET_PICKUP_DATE', value })}
              disabled={!canEditSchedule}
              max={form.deliveryDate || undefined}
            />
            {form.nextStatus === 'READY_FOR_PICKUP' && !form.pickupDate && (
              <div className="error-text" style={{ marginTop: 4 }}>
                Required before marking the load ready for pickup.
              </div>
            )}
          </div>

          <div className="form-control">
            <div className="flex-between" style={{ alignItems: 'center' }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <CopyButton value={isoToDisplay(form.deliveryDate)} label="Delivery date" />
                <label>Delivery Date</label>
              </span>
              <button
                type="button"
                className="copy-pickup-link"
                onClick={() => dispatch({ type: 'SET_DELIVERY_DATE', value: form.pickupDate })}
                disabled={!canEditSchedule || !form.pickupDate}
                title={
                  !form.pickupDate
                    ? 'Set a pickup date first'
                    : 'Copy the pickup date into the delivery date'
                }
              >
                Copy pickup date
              </button>
            </div>
            <DateField
              className="select-lg"
              clearable
              value={form.deliveryDate}
              onChange={(value) => dispatch({ type: 'SET_DELIVERY_DATE', value })}
              disabled={!canEditSchedule}
              min={form.pickupDate || undefined}
            />
            {fuelSurcharge && (
              <div className="muted tiny" style={{ marginTop: 4 }}>
                Fuel surcharge: {describeSurcharge(fuelSurcharge)}
                {fuelSurcharge.basis === 'clamped'
                  ? fuelSurcharge.week.isCurrent
                    ? ' (current rate)'
                    : ' (nearest week)'
                  : ''}
              </div>
            )}
          </div>
        </div>
      )}

      <div className="form-control form-control--note">
        <label>Pickup Note (optional)</label>
        <textarea
          className="select-lg"
          value={form.pickupNote}
          onChange={(e) => dispatch({ type: 'SET_PICKUP_NOTE', value: e.target.value })}
          disabled={!canEditAttributes}
          placeholder="Additional information for the pickup"
        />
      </div>

      <div className="form-control form-control--note">
        <label>Delivery Note (optional)</label>
        <textarea
          className="select-lg"
          value={form.deliveryNote}
          onChange={(e) => dispatch({ type: 'SET_DELIVERY_NOTE', value: e.target.value })}
          disabled={!canEditAttributes}
          placeholder="Additional information for the delivery"
        />
      </div>

      {pricingBreakdown && (
        <div className="form-control" style={{ gridColumn: '1 / -1' }}>
          <PricingBreakdownView
            breakdown={pricingBreakdown}
            surchargeBasis={fuelSurcharge?.basis ?? 'clamped'}
            internalOnly={!pricingInputs?.showPricingToCustomer}
          />
        </div>
      )}
    </div>
  );
}

interface SavedOrderTag {
  id: string;
  name: string;
}

interface OrderTagsEditorProps {
  tags: string[];

  savedTags?: SavedOrderTag[];

  savedTagsLoading?: boolean;

  savingTag?: boolean;

  deletingTag?: boolean;

  disabled?: boolean;

  onChange: (tags: string[]) => void;

  onSaveTag?: (tag: string) => Promise<void> | void;

  onDeleteSavedTag?: (tagId: string) => Promise<void> | void;
}

function OrderTagsEditor({
  tags,

  savedTags = [],

  savedTagsLoading = false,

  savingTag = false,

  deletingTag = false,

  disabled = false,

  onChange,

  onSaveTag,

  onDeleteSavedTag,
}: OrderTagsEditorProps) {
  const [value, setValue] = useState('');

  const [expanded, setExpanded] = useState(false);

  const [suggestionsOpen, setSuggestionsOpen] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);

  const MAX_VISIBLE_TAGS = 4;

  const normalizedValue = value.trim().toLowerCase().slice(0, 50);

  /*
   * Existing order tags are excluded from
   * suggestions because selecting them again
   * would do nothing.
   */
  const availableSavedTags = useMemo(() => {
    const assigned = new Set(tags.map((tag) => tag.toLowerCase()));

    const query = normalizedValue;

    return savedTags.filter((savedTag) => {
      if (assigned.has(savedTag.name.toLowerCase())) {
        return false;
      }

      if (!query) {
        return true;
      }

      return savedTag.name.toLowerCase().includes(query);
    });
  }, [tags, savedTags, normalizedValue]);

  const exactSavedTag = useMemo(
    () => savedTags.find((savedTag) => savedTag.name.toLowerCase() === normalizedValue) ?? null,
    [savedTags, normalizedValue]
  );

  const tagAlreadyAssigned = normalizedValue
    ? tags.some((tag) => tag.toLowerCase() === normalizedValue)
    : false;

  const canSaveCurrentValue = Boolean(normalizedValue && !exactSavedTag && onSaveTag);

  const addTag = (rawValue: string) => {
    /*
     * Keep UI canonical with the backend:
     * saved/order tags are lowercase.
     */
    const tag = rawValue.trim().toLowerCase().slice(0, 50);

    if (!tag) {
      return;
    }

    const alreadyExists = tags.some((existing) => existing.toLowerCase() === tag);

    if (alreadyExists) {
      setValue('');
      return;
    }

    /*
     * Same practical limit as backend normalization.
     */
    if (tags.length >= 20) {
      toast.warn('An order can have at most 20 tags.');
      return;
    }

    onChange([...tags, tag]);

    setValue('');
    setSuggestionsOpen(false);
  };

  const removeTag = (tagToRemove: string) => {
    onChange(tags.filter((tag) => tag !== tagToRemove));
  };

  const handleSaveCurrentTag = async () => {
    if (!canSaveCurrentValue || !onSaveTag) {
      return;
    }

    await onSaveTag(normalizedValue);

    /*
     * Saving a reusable tag doesn't automatically
     * need to clear or add it.
     *
     * But in this context the user started typing it
     * as an order tag, so adding it too is the most
     * natural interaction.
     */
    if (!tagAlreadyAssigned) {
      addTag(normalizedValue);
    }
  };

  const visibleTags = expanded ? tags : tags.slice(0, MAX_VISIBLE_TAGS);

  const hiddenCount = Math.max(0, tags.length - MAX_VISIBLE_TAGS);

  /*
   * Close suggestions when clicking outside.
   */
  useEffect(() => {
    if (!suggestionsOpen) {
      return;
    }

    const onMouseDown = (event: MouseEvent) => {
      const target = event.target as Node;

      if (containerRef.current && !containerRef.current.contains(target)) {
        setSuggestionsOpen(false);
      }
    };

    document.addEventListener('mousedown', onMouseDown);

    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [suggestionsOpen]);

  return (
    <div className="order-tags" ref={containerRef}>
      <div className={`order-tags__control${expanded ? ' order-tags__control--expanded' : ''}`}>
        <div className="order-tags__chips">
          {visibleTags.map((tag) => (
            <span key={tag} className="order-tag" title={tag}>
              <span className="order-tag__text">{tag}</span>

              {!disabled && (
                <button
                  type="button"
                  className="order-tag__remove"
                  onClick={() => removeTag(tag)}
                  aria-label={`Remove ${tag}`}
                  title={`Remove ${tag}`}
                >
                  ×
                </button>
              )}
            </span>
          ))}

          {!expanded && hiddenCount > 0 && (
            <button
              type="button"
              className="order-tags__more"
              onClick={() => setExpanded(true)}
              title="Show all tags"
            >
              +{hiddenCount} more
            </button>
          )}

          {expanded && tags.length > MAX_VISIBLE_TAGS && (
            <button type="button" className="order-tags__more" onClick={() => setExpanded(false)}>
              Show less
            </button>
          )}
        </div>

        {!disabled && (
          <div className="order-tags__input-wrap">
            <input
              className="order-tags__input"
              type="text"
              value={value}
              placeholder="Add tag…"
              maxLength={50}
              onFocus={() => setSuggestionsOpen(true)}
              onChange={(e) => {
                const next = e.target.value;

                if (next.endsWith(',')) {
                  addTag(next.slice(0, -1));

                  return;
                }

                setValue(next);
                setSuggestionsOpen(true);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();

                  addTag(value);
                }

                if (e.key === 'Backspace' && !value && tags.length) {
                  removeTag(tags[tags.length - 1]);
                }

                if (e.key === 'Escape') {
                  setSuggestionsOpen(false);
                }
              }}
            />

            {suggestionsOpen && (
              <div className="order-tags__suggestions">
                <div className="order-tags__suggestions-header">
                  <span>Saved tags</span>
                </div>

                {savedTagsLoading ? (
                  <div className="order-tags__suggestions-empty">Loading…</div>
                ) : (
                  <>
                    {availableSavedTags.length > 0 && (
                      <div className="order-tags__saved-list">
                        {availableSavedTags.map((savedTag) => (
                          <div key={savedTag.id} className="order-tags__saved-row">
                            <button
                              type="button"
                              className="order-tags__saved-select"
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={() => addTag(savedTag.name)}
                            >
                              {savedTag.name}
                            </button>

                            {onDeleteSavedTag && (
                              <button
                                type="button"
                                className="order-tags__saved-remove"
                                title={`Remove "${savedTag.name}" from saved tags`}
                                aria-label={`Remove ${savedTag.name} from saved tags`}
                                disabled={deletingTag}
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => onDeleteSavedTag(savedTag.id)}
                              >
                                ×
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    {normalizedValue && !tagAlreadyAssigned && (
                      <button
                        type="button"
                        className="order-tags__create"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => addTag(normalizedValue)}
                      >
                        Add "{normalizedValue}"
                      </button>
                    )}

                    {canSaveCurrentValue && (
                      <button
                        type="button"
                        className="order-tags__save"
                        disabled={savingTag}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={handleSaveCurrentTag}
                      >
                        {savingTag ? 'Saving…' : `Save "${normalizedValue}" for future use`}
                      </button>
                    )}

                    {!normalizedValue && availableSavedTags.length === 0 && (
                      <div className="order-tags__suggestions-empty">
                        No saved tags yet. Type a tag to create one.
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── OrderAuditLogPanel ───────────────────────────────────────────────────────

function auditActorLabel(log: {
  actorName?: string | null;
  actorEmail?: string | null;
  actorRole?: string | null;
}) {
  const roleLabel =
    log.actorRole === 'ORG_ADMIN'
      ? 'Org Admin'
      : log.actorRole === 'ORG_OWNER'
        ? 'Org Owner'
        : log.actorRole === 'CUSTOMER'
          ? 'Customer'
          : log.actorRole === 'SUBUSER'
            ? 'Customer Subuser'
            : log.actorRole?.replace(/_/g, ' ') || 'Unknown';

  const name = log.actorName?.trim();
  const email = log.actorEmail?.trim();

  if (name && email) return `${name} (${email}) — ${roleLabel}`;
  if (name) return `${name} — ${roleLabel}`;
  if (email) return `${email} — ${roleLabel}`;

  return roleLabel;
}

function formatAuditDate(value: string) {
  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function hasAuditDetails(value: unknown) {
  if (value == null) return false;

  if (Array.isArray(value)) {
    return value.length > 0;
  }

  if (typeof value === 'object') {
    return Object.keys(value as Record<string, unknown>).length > 0;
  }

  return true;
}

function AuditJsonBlock({ label, value }: { label: string; value: unknown }) {
  if (!hasAuditDetails(value)) {
    return null;
  }

  return (
    <div className="order-audit-log__json-block">
      <div className="order-audit-log__json-label">{label}</div>
      <pre className="order-audit-log__json">{JSON.stringify(value, null, 2)}</pre>
    </div>
  );
}

function OrderAuditLogPanel({
  orderId,
  orderItemId,
}: {
  orderId?: string | null;
  orderItemId?: string | null;
}) {
  const { data, isLoading, isFetching, isError, refetch } = useOrgOrderAuditLog(
    orderId,
    orderItemId
  );

  const logs = data?.logs ?? [];

  return (
    <details className="order-audit-log">
      <summary className="order-audit-log__header">
        <span className="order-audit-log__header-main">
          <span className="order-audit-log__title">Audit log</span>
          <span className="muted tiny">Complete history for this load, newest first.</span>
        </span>

        <span className="order-audit-log__header-actions">
          {!isLoading && !isError && (
            <span className="order-audit-log__count">
              {logs.length} {logs.length === 1 ? 'event' : 'events'}
            </span>
          )}
          <ChevronRight size={16} className="order-audit-log__chevron" aria-hidden="true" />
        </span>
      </summary>

      <div className="order-audit-log__content">
        <div className="order-audit-log__toolbar">
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={() => void refetch()}
            disabled={isFetching}
          >
            {isFetching && !isLoading ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>

        {isLoading ? (
          <div className="muted order-audit-log__empty">Loading audit log…</div>
        ) : isError ? (
          <div className="order-audit-log__error">
            Failed to load the audit log.
            <button type="button" className="btn btn--link" onClick={() => void refetch()}>
              Try again
            </button>
          </div>
        ) : logs.length === 0 ? (
          <div className="muted order-audit-log__empty">
            No audit events have been recorded for this load.
          </div>
        ) : (
          <div className="order-audit-log__timeline">
            {logs.map((log) => {
              const hasDetails =
                hasAuditDetails(log.diff) ||
                hasAuditDetails(log.before) ||
                hasAuditDetails(log.after) ||
                hasAuditDetails(log.meta);

              return (
                <article key={log.id} className="order-audit-log__event">
                  <div className="order-audit-log__rail" aria-hidden="true">
                    <span className="order-audit-log__dot" />
                  </div>

                  <div className="order-audit-log__event-body">
                    <div className="order-audit-log__event-top">
                      <span
                        className={`order-audit-log__action order-audit-log__action--${log.action.toLowerCase()}`}
                      >
                        {log.action.replace(/_/g, ' ')}
                      </span>

                      <span className="muted tiny">{formatAuditDate(log.createdAt)}</span>
                    </div>

                    <div className="order-audit-log__message">
                      {log.message || `${log.entityType} ${log.action.toLowerCase()}`}
                    </div>

                    <div className="muted tiny order-audit-log__actor">{auditActorLabel(log)}</div>

                    {hasDetails && (
                      <details className="order-audit-log__details">
                        <summary>View details</summary>

                        <div className="order-audit-log__details-body">
                          <AuditJsonBlock label="Changes" value={log.diff} />
                          <AuditJsonBlock label="Before" value={log.before} />
                          <AuditJsonBlock label="After" value={log.after} />
                          <AuditJsonBlock label="Metadata" value={log.meta} />
                        </div>
                      </details>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>
    </details>
  );
}

// ─── ManageItemDialog ─────────────────────────────────────────────────────────

interface ManageItemDialogProps {
  item: any;
  onClose: () => void;
  /** Resolves false when the save failed, so the dirty baseline is left alone. */
  onSave: (payload: any, isApprovedAlready: boolean) => Promise<boolean | void>;
  onReject: (payload: { orderId: string; itemId: string; reason?: string }) => Promise<void>;
  isSaving?: boolean;
  isRejecting?: boolean;
  onReplicatedItem?: (item: any) => void;
  onPrevious?: () => void;
  onNext?: () => void;
  hasPrevious?: boolean;
  hasNext?: boolean;
  currentPosition?: number;
  totalItems?: number;
  isNavigating?: boolean;
}

/** Outer wrapper — keys the inner component on item.id so state fully resets on item switch. */
export function ManageItemDialog(props: ManageItemDialogProps) {
  return <ManageItemDialogInner key={props.item.id} {...props} />;
}

function ManageItemDialogInner({
  item,
  onClose,
  onSave,
  onReject,
  isSaving,
  isRejecting,
  onReplicatedItem,
  onPrevious,
  onNext,
  hasPrevious,
  hasNext,
  currentPosition,
  totalItems,
  isNavigating = false,
}: ManageItemDialogProps) {
  // ── Server data ──────────────────────────────────────────────────────────
  // The customer whose addresses/catalog the modal shows. Starts as the order's
  // customer; a switch points it (and the Ship To list) at the new customer.
  const [selectedCustomerId, setSelectedCustomerId] = useState<string>(
    item.order?.customerId || ''
  );
  // Reset when the dialog moves to a different load.
  useEffect(() => {
    setSelectedCustomerId(item.order?.customerId || '');
  }, [item.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const { data: addressesData, refetch: refetchAddresses } = useListAddresses(
    selectedCustomerId || null
  );

  // Existing customers to switch between (single org, so one page covers it).
  // Exclude archived/rejected — an order should never be reassigned to one.
  const { data: customersData } = useListCustomers({ page: 1, pageSize: 1000, search: '' });
  const customerOptions = useMemo(
    () =>
      ((customersData as any)?.customers ?? [])
        .filter((c: any) => !c.archivedAt && c.status !== 'ARCHIVED' && c.status !== 'REJECTED')
        .map((c: any) => ({
          id: c.id,
          name: c.name || c.email || 'Customer',
        })),
    [customersData]
  );

  /*
   * Organization products are only the fallback when per-item pricing is off.
   * In that mode every active organization product is enabled by definition.
   *
   * When per-item pricing is on, product access comes exclusively from the
   * selected DeliveryAddress assignments returned by useListAddresses.
   */
  const { data: organizationProductsData, isFetching: organizationProductsFetching } =
    useListProducts({
      sortingKey: 'updatedAt',
      sortingOrder: 'desc',
      searchQuery: '',
      page: 1,
      pageSize: 1000,
      loadStatus: 'all',
      problemFirst: false,
      archived: 'exclude',
    });
  const { data: allLocationsData } = useListLocations();
  const { data: carriersData } = useListCarriers();
  const { data: organizationLoadOptions = [], isPending: orgLoadOptionsPending } =
    useListLoadOptions();
  const generateBOL = useGenerateBOL();
  const sendBOLMutation = useSendBOL();
  const removeCarrierMutation = useRemoveCarrier();
  const previewBOL = usePreviewBOL();
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const { data: savedOrderTags = [], isPending: savedOrderTagsPending } = useSavedOrderTags();

  const saveOrderTag = useSaveOrderTag();

  const deleteSavedOrderTag = useDeleteSavedOrderTag();

  const enableCustomerLocationLoadOption = useEnableCustomerLocationLoadOption();
  const disableCustomerLocationLoadOption = useDisableCustomerLocationLoadOption();

  const organizationDefaultLoadOption = useMemo(() => {
    if (!Array.isArray(organizationLoadOptions)) {
      return null;
    }

    return (
      organizationLoadOptions.find(
        (option: any) => option.isDefault === true && option.active !== false && !option.deletedAt
      ) ?? null
    );
  }, [organizationLoadOptions]);

  const deliveryAddresses = useMemo(
    () => addressesData?.addresses ?? [],
    [addressesData?.addresses]
  );
  const allCarriers = useMemo(() => carriersData?.carriers ?? [], [carriersData]);

  // ── Form state ───────────────────────────────────────────────────────────
  /**
   * The form and its dirty baseline, owned by `useEditSession` (unit-tested in
   * `hooks/__tests__/editSession.test.ts`).
   *
   * This replaced a hand-rolled baseline ref plus a two-phase save detector that
   * watched `isSaving` fall and then waited for a fresh `item` prop to arrive
   * before clearing dirtiness. That second phase never fired when the save moved
   * the load out of the table's current filter, because the row it was waiting
   * for never came back — so a load that had saved cleanly kept reporting
   * unsaved changes. `rebaseline` is now called explicitly by `handleSave` on a
   * confirmed save and depends on no refetch at all.
   *
   * `applyAutoChange` keeps its old meaning: the component reconciling itself
   * (fulfilment method against Ship To, pallet size against product) moves the
   * baseline with the dispatch, so dirtiness only ever reflects a human. A plain
   * `dispatch` from inside a `useEffect` in this component is almost always a
   * bug.
   */
  const {
    form,
    dispatch,
    baseline,
    dirty,
    isDirty: formIsDirty,
    applyAutoChange,
    rebaseline,
  } = useEditSession<FormState, FormAction>({
    reducer: formReducer,
    initialState: useMemo(() => buildInitialState(item), [item.id]), // eslint-disable-line react-hooks/exhaustive-deps
    resetAction: RESET_FORM,
    ignore: UNTRACKED_FORM_FIELDS,
  });

  /*
   * A staged carrier counts as unsaved work even when no tracked field moved:
   * `ASSIGN_CARRIER` and `REMOVE_CARRIER` both set `pendingCarrierId`, and until
   * a save applies it there is something to lose by closing.
   */
  const hasUnsavedChanges = formIsDirty || form.pendingCarrierId !== undefined;

  /** e.g. "Pickup date, Internal notes, Carrier" — what closing would discard. */
  const unsavedSummary = useMemo(() => {
    const fields = describeDirtyFields(dirty, FORM_FIELD_LABELS);
    if (form.pendingCarrierId === undefined) return fields;
    return fields ? `${fields}, Carrier` : 'Carrier';
  }, [dirty, form.pendingCarrierId]);

  const confirmDiscard = (question: string) =>
    window.confirm(
      unsavedSummary ? `Unsaved changes: ${unsavedSummary}.\n\n${question}` : question
    );

  // ── Derived item data ────────────────────────────────────────────────────
  /*
   * `item` comes from the parent table and can remain a stale snapshot briefly
   * after a mutation. Keep the persisted status/date locally so an open dialog
   * reflects a successful save immediately, then sync from the parent again
   * when its refetch arrives.
   */
  const [persistedStatus, setPersistedStatus] = useState<string>(item.status as string);

  const [persistedOrderCreatedAt, setPersistedOrderCreatedAt] = useState<string | null>(
    item.order?.createdAt ?? null
  );

  useEffect(() => {
    setPersistedStatus(item.status as string);
  }, [item.status]);

  useEffect(() => {
    setPersistedOrderCreatedAt(item.order?.createdAt ?? null);
  }, [item.order?.createdAt]);

  const status = persistedStatus;
  const isWaitlist = status === 'WAITLIST' || status === 'OUT_OF_WAREHOUSE_REQUESTED';
  const isProductDeleted = item.product?.deletedAt != null;
  const isApprovedAlready = status !== 'PENDING' && status !== 'WAITLIST';

  /*
   * PREPAY_REQUIRED is editable through the normal update endpoint, but it
   * additionally exposes an Approve action. Only genuinely unapproved loads
   * use Approve as their single primary save action.
   */
  const isPrepayRequiredStatus = status === 'PREPAY_REQUIRED';
  const usesApproveAction = !isApprovedAlready;

  const hidePrice = item.order?.organization?.hidePrice === true;
  // Prepay loads run PREPAY_REQUIRED → PAID → ASSIGNED, which is the only
  // reason `PAID` can mean pre-dispatch rather than finished.
  const requiresPrepay = item.cust?.requiresPrepay === true;

  // Two separate questions: who may take a carrier, and whether taking one
  // advances the load. Everything but PENDING may; far fewer advance.
  const canAssignCarrier = canAssignCarrierFromStatus(status);
  const carrierAssignmentAdvancesStatus = assigningCarrierAdvancesStatus(status, requiresPrepay);
  const canEditBase = status !== 'CUSTOMER_CANCELLED' && status !== 'REJECTED';
  const canEdit = canEditBase && !isSaving && !isRejecting;
  const canEditAttributes = canEdit && status !== 'SHIPPED';
  const canChangeStatus = !isSaving && !isRejecting && !isProductDeleted && status !== 'PENDING';

  // ── Customer switch ────────────────────────────────────────────────────────
  const originalCustomerId = item.order?.customerId ?? '';
  const customerChanged = form.customerId !== originalCustomerId;
  const orderLoadCount = item.order?._count?.items ?? 1;
  // Name to show: the picked customer when switched, else the order's customer.
  const effectiveCustomerName = customerChanged
    ? (customerOptions.find((c: any) => c.id === form.customerId)?.name ?? 'Selected customer')
    : (item.cust?.name ?? '');
  const effectiveCustomerId = customerChanged ? form.customerId : originalCustomerId;

  // Switching resets Ship To (old address belongs to the old customer) and
  // re-points the address list; the admin then picks a new Ship To to save.
  const onSwitchCustomer = (nextCustomerId: string) => {
    if (!nextCustomerId || nextCustomerId === form.customerId) {
      setShowCustomerSwitch(false);
      return;
    }
    setSelectedCustomerId(nextCustomerId);
    dispatch({ type: 'SET_CUSTOMER', customerId: nextCustomerId });
    setShowCustomerSwitch(false);
  };

  // After a switch (Ship To cleared), scroll the Ship To field into view so the
  // admin sees what to fill in next. Runs once the new addresses have loaded.
  const shipToRef = useRef<HTMLDivElement>(null);
  const didScrollToShipTo = useRef(false);
  useEffect(() => {
    if (!customerChanged || form.deliveryAddressId) {
      didScrollToShipTo.current = false;
      return;
    }
    if (didScrollToShipTo.current || !shipToRef.current) return;
    didScrollToShipTo.current = true;
    shipToRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [customerChanged, form.deliveryAddressId, deliveryAddresses]);

  // ── Derived product / location data ─────────────────────────────────────
  const perItemPricing = (addressesData as any)?.perItemPricing === true;

  /*
   * Product access now follows Ship To / DeliveryAddress.
   *
   * DeliveryAddress
   *   -> CustomerLocationProduct
   *   -> ENABLED / PREVIEW / DISABLED
   *
   * `Customer.locationId` remains the warehouse used elsewhere for stock and
   * ProductLoadPrice. It is deliberately not used to decide product access.
   */
  // After a customer switch, the old order address belongs to the old customer,
  // so never fall back to it — managing products must target the new Ship To only.
  const productAccessAddressId = customerChanged
    ? form.deliveryAddressId
    : form.deliveryAddressId || item.order?.deliveryAddress?.id || '';

  const productAccessAddress = useMemo(() => {
    if (!productAccessAddressId) {
      return null;
    }

    return (
      deliveryAddresses.find((address: any) => address.id === productAccessAddressId) ??
      (!customerChanged && item.order?.deliveryAddress?.id === productAccessAddressId
        ? item.order.deliveryAddress
        : null)
    );
  }, [productAccessAddressId, deliveryAddresses, item.order?.deliveryAddress, customerChanged]);

  const productAccessById = useMemo(() => {
    const result = new Map<string, ProductLocationAccessState>();

    if (!perItemPricing) {
      return result;
    }

    const assignments = Array.isArray(productAccessAddress?.products)
      ? productAccessAddress.products
      : [];

    for (const assignment of assignments) {
      if (!assignment?.productId) continue;

      if (assignment.isEnabled) {
        result.set(assignment.productId, 'ENABLED');
        continue;
      }

      if (assignment.isPreview) {
        result.set(assignment.productId, 'PREVIEW');
      }
    }

    return result;
  }, [perItemPricing, productAccessAddress]);

  const [enablingProduct, setEnablingProduct] = useState(false);
  const [showProductManager, setShowProductManager] = useState(false);
  // Reveals the customer-switch dropdown in the header.
  const [showCustomerSwitch, setShowCustomerSwitch] = useState(false);
  const [productSearch, setProductSearch] = useState('');
  const debouncedProductSearch = useDebouncedSearch(productSearch);

  const setLocationProductAccess = useSetCustomerLocationProductAccess();

  /*
   * The order editor needs full product metadata (especially palletSizes), so
   * use the organization product list for the choices and overlay the selected
   * DeliveryAddress's CustomerLocationProduct state onto it.
   *
   * This replaces the old customer-wide catalog completely.
   */
  const organizationProductOptions = useMemo<CustomerLocationCatalogProduct[]>(
    () =>
      ((organizationProductsData as any)?.products ?? []).map((product: any) => ({
        ...product,
        access: perItemPricing
          ? (productAccessById.get(product.id) ?? 'DISABLED')
          : ('ENABLED' as const),
      })),
    [organizationProductsData, perItemPricing, productAccessById]
  );

  const productOptions = useMemo(() => {
    const currentProduct = item.product;

    if (
      currentProduct &&
      !organizationProductOptions.some((product) => product.id === currentProduct.id)
    ) {
      return [
        {
          id: currentProduct.id,
          name: currentProduct.name,
          imageUrl: currentProduct.imageUrl ?? fallbackProductImage,
          deliveryType: currentProduct.deliveryType,
          palletSizes: currentProduct.palletSizes ?? [],
          access: perItemPricing
            ? (productAccessById.get(currentProduct.id) ?? 'DISABLED')
            : ('ENABLED' as const),
        },
        ...organizationProductOptions,
      ];
    }

    return organizationProductOptions;
  }, [organizationProductOptions, item.product, perItemPricing, productAccessById]);

  const managerProducts = useMemo<CustomerLocationCatalogProduct[]>(() => {
    const query = debouncedProductSearch.trim().toLowerCase();

    if (!query) {
      return productOptions;
    }

    return productOptions.filter((product: any) =>
      [product.name, product.description]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query))
    );
  }, [productOptions, debouncedProductSearch]);

  const managerGroups = useMemo(
    () => splitProductsByLocationAccess(managerProducts),
    [managerProducts]
  );

  const productManagerFetching = organizationProductsFetching;

  const closeProductManager = useCallback(() => {
    setShowProductManager(false);
    setProductSearch('');
  }, []);

  /*
   * A manager opened for Brooklyn must never stay open showing Brooklyn access
   * after Ship To changes to Queens.
   */
  useEffect(() => {
    closeProductManager();
  }, [productAccessAddressId, closeProductManager]);

  const handlePickProduct = async (productId: string, access: ProductLocationAccessState) => {
    if (!productId || enablingProduct) return;

    if (!productAccessAddressId) {
      toast.warn('Select a Ship To location before managing product access.');
      return;
    }

    /*
     * When per-item pricing is disabled, every active organization product is
     * already enabled. There is no CustomerLocationProduct write to perform.
     */
    if (!perItemPricing || access === 'ENABLED') {
      dispatch({ type: 'SET_PRODUCT', value: productId });
      return;
    }

    setEnablingProduct(true);

    try {
      await setLocationProductAccess.mutateAsync({
        addressId: productAccessAddressId,
        productId,
        isEnabled: true,
        isPreview: false,
      });

      /*
       * The address response carries that location's product assignments, so a
       * single refetch refreshes the access state used by both the manager and
       * the save gate.
       */
      await refetchAddresses();

      dispatch({ type: 'SET_PRODUCT', value: productId });

      toast.success('Product enabled for this delivery location.');
    } catch (error: any) {
      toast.error(
        error?.response?.data?.error || 'Could not enable that product for this delivery location.'
      );
    } finally {
      setEnablingProduct(false);
    }
  };

  const selectedProduct = useMemo(() => {
    if (
      !form.selectedProductId ||
      form.selectedProductId === (item.product?.id ?? item.productId)
    ) {
      return item.product;
    }

    return (
      productOptions.find((product: any) => product.id === form.selectedProductId) ?? item.product
    );
  }, [form.selectedProductId, item.product, item.productId, productOptions]);

  const palletSizes = useMemo(() => {
    if (form.selectedProductId === (item.product?.id ?? item.productId)) {
      return (item.product?.palletSizes ?? []) as any[];
    }

    const fromCatalog = productOptions.find(
      (product: any) => product.id === form.selectedProductId
    );

    return (fromCatalog?.palletSizes ?? []) as any[];
  }, [form.selectedProductId, item.product, item.productId, productOptions]);

  const isPallet = selectedProduct?.deliveryType === 'PALLET';

  const selectedProductLocationAccess = useMemo<ProductLocationAccessState | null>(() => {
    if (!form.selectedProductId) return null;
    if (!perItemPricing) return 'ENABLED';

    return productAccessById.get(form.selectedProductId) ?? 'DISABLED';
  }, [form.selectedProductId, perItemPricing, productAccessById]);

  const productLocationAccessReady = !perItemPricing || Boolean(addressesData);

  /*
   * Changing Ship To does NOT silently enable the current product at the new
   * location. The admin must explicitly enable it or choose another product.
   */
  const selectedProductNotEnabledAtShipTo = Boolean(
    isApprovedAlready &&
    perItemPricing &&
    productAccessAddressId &&
    form.selectedProductId &&
    productLocationAccessReady &&
    selectedProductLocationAccess !== 'ENABLED'
  );

  // ── Copy the product name ───────────────────────────────────────────────
  // Reads from selectedProduct, not item.product, so the button copies what the
  // title is currently showing — including a product picked in the dropdown but
  // not yet saved.
  const productName = selectedProduct?.name ?? item.product?.name ?? '';

  // Auto-reset palletSizeId when product changes to a non-pallet or different pallet
  useEffect(() => {
    const defaultId = !isPallet
      ? ''
      : form.selectedProductId === (item.product?.id ?? item.productId)
        ? (item.palletIdSnapshot ?? palletSizes[0]?.id ?? '')
        : (palletSizes[0]?.id ?? '');

    if (defaultId !== form.palletSizeId) {
      applyAutoChange({ type: 'SET_PALLET_SIZE', value: defaultId }, { palletSizeId: defaultId });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.selectedProductId, isPallet]);

  const locationOptions = useMemo(
    () => (item.choices?.locations ?? []) as Array<{ id: string; name: string; line1?: string }>,
    [item.choices?.locations]
  );

  // Every other active warehouse: an admin can override a load onto one of these
  // as a one-off. Picking one does not assign it to the customer.
  const overrideLocationOptions = useMemo(() => {
    const assignedIds = new Set(locationOptions.map((l) => l.id));
    return ((allLocationsData as any[]) ?? [])
      .filter((l) => !l.deletedAt && !assignedIds.has(l.id))
      .map((l) => ({ id: l.id as string, name: l.name as string, line1: l.line1 as string }));
  }, [allLocationsData, locationOptions]);

  const selectedLocation = useMemo(() => {
    if (!form.locationId) return item.location ?? null;
    if (form.locationId === item.location?.id) return item.location;
    const full = (allLocationsData as any[])?.find((l: any) => l.id === form.locationId);
    if (full) return full;
    return locationOptions.find((l) => l.id === form.locationId) ?? item.location ?? null;
  }, [form.locationId, item.location, allLocationsData, locationOptions]);

  const selectedDeliveryAddress = useMemo(() => {
    // After a customer switch the old order address belongs to the old customer,
    // so don't fall back to it — show nothing until a new Ship To is chosen.
    if (!form.deliveryAddressId)
      return customerChanged ? null : (item.order?.deliveryAddress ?? null);
    return (
      deliveryAddresses.find((a: any) => a.id === form.deliveryAddressId) ??
      item.order?.deliveryAddress ??
      null
    );
  }, [form.deliveryAddressId, deliveryAddresses, item.order?.deliveryAddress, customerChanged]);

  const addressLoadOptions = useMemo(() => {
    const assignedOptions = Array.isArray(selectedDeliveryAddress?.loadOptions)
      ? selectedDeliveryAddress.loadOptions.filter((option: any) => option.active !== false)
      : [];

    const productChoices = Array.isArray(item?.choices?.loadOptions)
      ? item.choices.loadOptions
      : [];

    /*
     * If this delivery location has explicit
     * assignments, they remain the source of truth.
     */
    if (assignedOptions.length > 0) {
      return assignedOptions.map((assigned: any) => {
        const assignedId = assigned.orgLoadOptionId ?? assigned.id;

        const matchingChoice = productChoices.find(
          (choice: any) => (choice.orgLoadOptionId ?? choice.id) === assignedId
        );

        return {
          ...matchingChoice,
          ...assigned,

          id: assignedId,
          orgLoadOptionId: assignedId,

          name: assigned.name ?? matchingChoice?.name ?? assignedId,

          capacityLabel: assigned.capacityLabel ?? matchingChoice?.capacityLabel ?? null,

          isOrganizationDefault: false,
        };
      });
    }

    /*
     * No location-specific configuration:
     * use the organization-wide default.
     */
    if (!organizationDefaultLoadOption) {
      return [];
    }

    const defaultId = organizationDefaultLoadOption.id;

    const matchingChoice = productChoices.find(
      (choice: any) => (choice.orgLoadOptionId ?? choice.id) === defaultId
    );

    return [
      {
        ...matchingChoice,
        ...organizationDefaultLoadOption,

        id: defaultId,
        orgLoadOptionId: defaultId,

        name: organizationDefaultLoadOption.name ?? matchingChoice?.name ?? defaultId,

        capacityLabel:
          organizationDefaultLoadOption.capacityLabel ?? matchingChoice?.capacityLabel ?? null,

        isOrganizationDefault: true,
      },
    ];
  }, [selectedDeliveryAddress, item?.choices?.loadOptions, organizationDefaultLoadOption]);

  const hasLocationSpecificLoadOptions = Boolean(
    selectedDeliveryAddress?.loadOptions?.some((option: any) => option.active !== false)
  );

  const addressDefaultFulfillmentMethodId =
    selectedDeliveryAddress?.defaultFulfillmentMethodId ?? null;

  const effectiveDefaultFulfillmentMethodId = hasLocationSpecificLoadOptions
    ? addressDefaultFulfillmentMethodId
    : (organizationDefaultLoadOption?.id ?? null);

  const usingOrganizationDefault = Boolean(
    form.deliveryAddressId && !hasLocationSpecificLoadOptions && organizationDefaultLoadOption
  );

  const handleEnableLocationLoadOption = useCallback(
    async (loadOptionId: string) => {
      if (!form.deliveryAddressId) return;

      try {
        await enableCustomerLocationLoadOption.mutateAsync({
          addressId: form.deliveryAddressId,
          loadOptionId,
        });

        await refetchAddresses();

        dispatch({
          type: 'SET_LOAD_OPTION',
          value: loadOptionId,
        });

        toast.success('Fulfilment method enabled for this customer location.');
      } catch (error: any) {
        toast.error(
          error?.response?.data?.error ||
            'Failed to enable fulfilment method for this customer location.'
        );
      }
    },
    [form.deliveryAddressId, enableCustomerLocationLoadOption, refetchAddresses, dispatch]
  );

  const handleDisableLocationLoadOption = useCallback(
    async (loadOptionId: string) => {
      if (!form.deliveryAddressId) return;

      try {
        await disableCustomerLocationLoadOption.mutateAsync({
          addressId: form.deliveryAddressId,
          loadOptionId,
        });

        await refetchAddresses();

        toast.success('Fulfilment method disabled for this customer location.');
      } catch (error: any) {
        toast.error(
          error?.response?.data?.error ||
            'Failed to disable fulfilment method for this customer location.'
        );
      }
    },
    [form.deliveryAddressId, disableCustomerLocationLoadOption, refetchAddresses]
  );

  /*
   * Both sources behind `addressLoadOptions` arrive asynchronously, and until
   * they do an empty list means "not known yet", not "none assigned":
   *
   *  - The orders list endpoint selects delivery-address scalars only, so the
   *    fallback `item.order.deliveryAddress` carries no `loadOptions` at all
   *    until useListAddresses resolves.
   *  - The organization default is only known once useListLoadOptions resolves,
   *    and it is only consulted when the location has no methods of its own.
   *
   * Reconciling against either gap wipes a perfectly valid saved method and
   * opens the modal already reporting unsaved changes.
   */
  const loadOptionsReady =
    Array.isArray(selectedDeliveryAddress?.loadOptions) &&
    (hasLocationSpecificLoadOptions || !orgLoadOptionsPending);

  /*
   * Ship To controls the valid fulfilment methods. Keep the current method when
   * it remains available; otherwise move to the location default, then fall
   * back to the first assigned method.
   */
  useEffect(() => {
    if (!form.deliveryAddressId) {
      if (form.loadOptionId) {
        applyAutoChange({ type: 'SET_LOAD_OPTION', value: '' }, { loadOptionId: '' });
      }

      return;
    }

    if (!loadOptionsReady) {
      return;
    }

    const currentIsAvailable = addressLoadOptions.some(
      (option: any) => (option.orgLoadOptionId ?? option.id) === form.loadOptionId
    );

    if (currentIsAvailable) {
      return;
    }

    const defaultIsAvailable =
      Boolean(effectiveDefaultFulfillmentMethodId) &&
      addressLoadOptions.some(
        (option: any) =>
          (option.orgLoadOptionId ?? option.id) === effectiveDefaultFulfillmentMethodId
      );

    const nextLoadOptionId = defaultIsAvailable
      ? effectiveDefaultFulfillmentMethodId!
      : (addressLoadOptions[0]?.orgLoadOptionId ?? addressLoadOptions[0]?.id ?? '');

    if (nextLoadOptionId !== form.loadOptionId) {
      applyAutoChange(
        { type: 'SET_LOAD_OPTION', value: nextLoadOptionId },
        { loadOptionId: nextLoadOptionId }
      );
    }
  }, [
    form.deliveryAddressId,
    form.loadOptionId,
    addressLoadOptions,
    effectiveDefaultFulfillmentMethodId,
    loadOptionsReady,
    applyAutoChange,
  ]);

  const fullCarrierProfile = useMemo(
    () => allCarriers.find((c) => c.id === form.localCarrier?.id),
    [allCarriers, form.localCarrier?.id]
  );

  const orderShort = item.loadNumber || `#${item.order?.id?.slice(-6) || '—'}`;

  // ── Dirty checks ─────────────────────────────────────────────────────────
  // `hasUnsavedChanges` is computed with the edit session, above.
  //
  // `isBOLDirty` stays separate and narrower. It answers a different question —
  // "would the BOL print something other than what is stored" — so it looks only
  // at the fields the BOL actually renders, and deliberately omits
  // `loadOptionId`. Folding it into the session's dirty set would start warning
  // about a stale BOL over edits the BOL never shows.
  const hasBOLUnsaved = useMemo(() => isBOLDirty(form, baseline), [form, baseline]);

  // ── Validation ───────────────────────────────────────────────────────────
  // The rules themselves live in ./saveGate, where they are unit-tested. A
  // disabled button looks the same whichever condition produced it, so this is
  // the one piece of dialog state worth being able to assert directly.
  const commonSaveGateContext = {
    canEdit,
    isProductDeleted,
    isPallet,
    customerChanged,

    // The chosen Ship To sub-location mandates a PO number. Reads the live
    // address list, falling back to the load's current address.
    requiresPoNumber:
      deliveryAddresses.find((a: any) => a.id === form.deliveryAddressId)?.requiresPoNumber ===
        true ||
      ((item.order?.deliveryAddress as any)?.id === form.deliveryAddressId &&
        (item.order?.deliveryAddress as any)?.requiresPoNumber === true),
  };

  /*
   * Update and Approve intentionally use different validation modes.
   *
   * PREPAY_REQUIRED can now do either:
   *
   * Update  -> regular /update endpoint, status/date stay as edited.
   * Approve -> /approve endpoint, backend moves PREPAY_REQUIRED -> APPROVED
   *            and resets Order.createdAt to approval time.
   */
  const updateSaveGateContext = {
    ...commonSaveGateContext,
    isApprovedAlready: true,
  };

  const approveSaveGateContext = {
    ...commonSaveGateContext,
    isApprovedAlready: false,
  };

  const defaultSaveGateContext = usesApproveAction ? approveSaveGateContext : updateSaveGateContext;

  /*
   * Preserve the existing Replicate gate for the normal action mode.
   */
  const approveDisabled = isApproveDisabled(form, defaultSaveGateContext);

  // A load always needs a product. Switching the customer clears it, so block
  // saving until the admin re-picks one.
  const productMissing = !form.selectedProductId;
  const productBlockReason = productMissing
    ? 'Select a product for the new customer.'
    : selectedProductNotEnabledAtShipTo
      ? 'Selected product is not enabled at the current Ship To location.'
      : '';
  const productBlocksSave = productMissing || selectedProductNotEnabledAtShipTo;

  const prepayApproveDisabled =
    productBlocksSave ||
    isApproveDisabled(form, approveSaveGateContext) ||
    Boolean(isSaving || isRejecting);

  const prepayApproveDisabledTitle =
    productBlockReason || saveDisabledReason(form, approveSaveGateContext);

  const baseSaveDisabledTitle = saveDisabledReason(form, defaultSaveGateContext);

  const saveDisabledTitle = productBlockReason || baseSaveDisabledTitle;

  // Broader than the save gate: a changed status or a staged carrier is
  // saveable even on a read-only load. Exemptions live in ./saveGate.
  const primaryActionDisabled =
    productBlocksSave ||
    isPrimaryActionDisabled(form, defaultSaveGateContext, {
      statusChanged: form.nextStatus !== status,
      carrierChanged: form.pendingCarrierId !== undefined,
      isBusy: Boolean(isSaving || isRejecting),
    });

  // ── BOL actions ──────────────────────────────────────────────────────────
  const handleDownloadBOL = async () => {
    try {
      await generateBOL.mutateAsync({ itemId: item.id, loadNumber: item.loadNumber });
      toast.success('BOL downloaded successfully');
    } catch (e) {
      toast.error(await blobErrorMessage(e, 'Failed to download BOL'));
    }
  };

  const handlePreviewBOL = async () => {
    try {
      const url = await previewBOL.mutateAsync(item.id);
      setPreviewUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return url;
      });
    } catch (e) {
      toast.error(await blobErrorMessage(e, 'Failed to generate BOL preview'));
    }
  };

  // ── Customer email history ───────────────────────────────────────────────
  const [showEmailHistory, setShowEmailHistory] = useState(false);
  // Seconds are noise here, and the shorter string keeps the header stable.
  const formatEmailedAt = (iso: string) =>
    new Date(iso).toLocaleString(undefined, {
      month: 'numeric',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  const { data: notificationsData, refetch: refetchLoadNotifications } = useLoadNotifications(
    item.id
  );

  const loadNotifications = useMemo(
    () => notificationsData?.notifications ?? [],
    [notificationsData]
  );

  const pickupEmailedAt = useMemo(() => lastPickupEmailAt(loadNotifications), [loadNotifications]);

  /*
   * The actual mutation lives in features/org-orders/api with the rest of the
   * order-item API hooks. This component only owns UI feedback and refreshing
   * the notification-history query.
   */
  const notifyPickupCustomer = useNotifyPickupCustomer();

  // "Notify customer" opens a recipient picker; sending happens from there.
  const [showPickupRecipients, setShowPickupRecipients] = useState(false);

  const pickupOrderId: string | undefined = item.order?.id ?? item.orderId;

  const handleNotifyPickupCustomer = async (recipients: string[], extraRecipients: string[]) => {
    const orderId = pickupOrderId;

    if (!orderId) {
      toast.error('Order id is missing for this load.');
      return;
    }

    try {
      await notifyPickupCustomer.mutateAsync({
        orderId,
        itemId: item.id,
        recipients,
        extraRecipients,
      });

      const sentCount = dedupeEmails([...recipients, ...extraRecipients]).length;

      setShowPickupRecipients(false);
      await refetchLoadNotifications();
      toast.success(
        sentCount === 1
          ? 'Pickup instructions emailed.'
          : `Pickup instructions emailed to ${sentCount} recipients.`
      );
    } catch (error: any) {
      const code = error?.response?.data?.error;

      if (code === 'PICKUP_NOTIFICATION_ALREADY_SENT') {
        setShowPickupRecipients(false);
        await refetchLoadNotifications();
        toast.info('Pickup instructions were already emailed for this load.');
        return;
      }

      if (code === 'LOAD_NOT_READY_FOR_PICKUP') {
        toast.warn('Save the load as Ready for Pickup before notifying the customer.');
        return;
      }

      if (code === 'PICKUP_RECIPIENT_OPTED_OUT') {
        toast.error(
          `${error?.response?.data?.recipient ?? 'A recipient'} has turned pickup emails off.`
        );
        return;
      }

      if (code === 'INVALID_PICKUP_RECIPIENT') {
        toast.error(
          `${error?.response?.data?.recipient ?? 'A recipient'} isn't a valid recipient.`
        );
        return;
      }

      if (code === 'NO_PICKUP_RECIPIENTS') {
        toast.error('Pick or type at least one recipient and try again.');
        return;
      }

      if (code === 'PICKUP_NOTIFICATION_NOT_SENT') {
        toast.error(
          'Pickup email was not sent. Check the customer email/notification settings and try again.'
        );
        return;
      }

      toast.error(code || error?.message || 'Failed to send pickup notification.');
    }
  };

  const canNotifyPickupCustomer =
    status === 'READY_FOR_PICKUP' &&
    !pickupEmailedAt &&
    !hasUnsavedChanges &&
    !isSaving &&
    !isRejecting;

  // ── Send BOL modal ───────────────────────────────────────────────────────
  const [showBOLModal, setShowBOLModal] = useState(false);

  const handleSendBOL = async () => {
    setShowBOLModal(true);
  };

  const handleSendBOLConfirm = async (emails: string[], note?: string, contactName?: string) => {
    try {
      const result = await sendBOLMutation.mutateAsync({
        itemId: item.id,
        emails,
        note,
        contactName: contactName || form.driverName || undefined,
      });

      reportBOLSendResult(result);
      setShowBOLModal(false);
    } catch (e) {
      toast.error(await blobErrorMessage(e, 'Failed to send BOL'));
    }
  };

  // ── Save ─────────────────────────────────────────────────────────────────
  const [saveIntent, setSaveIntent] = useState<'update' | 'approve' | null>(null);

  const handleSave = async (
    intent: 'update' | 'approve' = usesApproveAction ? 'approve' : 'update'
  ) => {
    if (productMissing) {
      toast.warn('Select a product for the new customer before saving.');
      return;
    }

    if (selectedProductNotEnabledAtShipTo) {
      toast.warn(
        'Enable the selected product at the current Ship To location or choose another product.'
      );
      return;
    }

    const payload = buildSavePayload(form, item);

    /*
     * onSave's second argument chooses the existing endpoint:
     * true  -> normal update
     * false -> approve
     */
    setSaveIntent(intent);

    try {
      const saved = await onSave(payload, intent === 'update');

      if (saved === false) return;

      /*
       * Adopt the persisted state immediately instead of waiting for the parent
       * row snapshot to refetch.
       *
       * PREPAY_REQUIRED approval is special: the approve endpoint forces the
       * item to APPROVED and resets Order.createdAt to the approval time.
       */
      if (intent === 'approve' && isPrepayRequiredStatus) {
        const approvedAt = new Date().toISOString();

        setPersistedStatus('APPROVED');
        setPersistedOrderCreatedAt(approvedAt);

        rebaseline({
          ...form,
          nextStatus: 'APPROVED',
          pendingCarrierId: undefined,
        });
      } else {
        if (intent === 'update') {
          setPersistedStatus(form.nextStatus);
        }

        rebaseline({
          ...form,
          pendingCarrierId: undefined,
        });
      }
    } finally {
      setSaveIntent(null);
    }
  };

  // ── Close ────────────────────────────────────────────────────────────────
  const handleClose = () => {
    if (hasUnsavedChanges) {
      if (!confirmDiscard('Leave without saving?')) {
        return;
      }
    }
    onClose();
  };

  const handleNavigate = (direction: 'previous' | 'next') => {
    if (isSaving || isRejecting) {
      return;
    }

    if (hasUnsavedChanges) {
      const confirmed = confirmDiscard('Continue to the next load without saving?');

      if (!confirmed) {
        return;
      }
    }

    if (direction === 'previous') {
      onPrevious?.();
      return;
    }

    onNext?.();
  };

  const closePreview = useCallback(() => {
    setPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  }, []);

  // ── Reject ───────────────────────────────────────────────────────────────
  const [showReject, setShowReject] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  // Mobile-only: collapse the pinned footer (note + action buttons) to give the
  // form more room. The toggle is hidden on desktop via CSS.
  const [footerCollapsed, setFooterCollapsed] = useState(false);

  // ── Replicate ────────────────────────────────────────────────────────────
  const [isReplicateDialogOpen, setIsReplicateDialogOpen] = useState(false);
  const [isInvoiceDialogOpen, setIsInvoiceDialogOpen] = useState(false);

  // ── Status guard ─────────────────────────────────────────────────────────
  const handleSetStatus = (s: string) => {
    if (s === 'REJECTED') return; // use the Reject button instead
    dispatch({ type: 'SET_STATUS', value: s });
  };

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className="modal modal--sheet" role="dialog" aria-modal="true">
      <div className="modal__backdrop" onClick={handleClose} />
      <div className="modal__panel manage-dialog">
        {isSaving && (
          <div className="manage-saving-overlay">
            <div className="spinner" />
            <div className="muted">Saving changes…</div>
          </div>
        )}

        {/* Header */}
        <div className="modal__header">
          <div className="manage-dialog__title-block">
            <h3 className="modal__title">
              Load #{orderShort}
              <CopyButton value={orderShort} label="Load number" />{' '}
              <span className="manage-dialog__product">
                <span className="manage-dialog__product-name">
                  {productMissing ? (
                    <span className="muted">Select a product</span>
                  ) : (
                    productName || item.product?.name || 'Product'
                  )}
                </span>

                {isApprovedAlready && canEditAttributes && item.order?.customerId && (
                  <button
                    type="button"
                    className="btn btn--link manage-dialog__manage-products"
                    onClick={() => {
                      if (!productAccessAddressId) {
                        toast.warn('Select a Ship To location first.');
                        return;
                      }

                      showProductManager ? closeProductManager() : setShowProductManager(true);
                    }}
                    disabled={enablingProduct || !productAccessAddressId}
                    title={
                      !productAccessAddressId
                        ? 'Select a Ship To location first'
                        : 'Manage products for this delivery location'
                    }
                  >
                    {showProductManager ? 'Done' : 'Manage products'}
                  </button>
                )}

                <CopyButton value={productName} label="Product name" />
              </span>
            </h3>
            {(item.cust?.name || form.customerId) && (
              <div className="manage-dialog__customer">
                {requiresPrepay && !customerChanged && (
                  <span
                    className="manage-dialog__prepay"
                    title="Requires prepayment"
                    aria-label="Requires prepayment"
                  >
                    $
                  </span>
                )}

                {showCustomerSwitch ? (
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      flexWrap: 'wrap',
                    }}
                  >
                    <span style={{ minWidth: 240, display: 'inline-block' }}>
                      <SearchableSelect
                        value={form.customerId}
                        options={customerOptions}
                        onChange={onSwitchCustomer}
                        placeholder="Search customers…"
                        ariaLabel="Switch customer"
                      />
                    </span>
                    <button
                      type="button"
                      className="btn btn--link"
                      onClick={() => setShowCustomerSwitch(false)}
                    >
                      Done
                    </button>
                  </span>
                ) : (
                  <>
                    <NavLink
                      to={`/customers?customerId=${encodeURIComponent(effectiveCustomerId)}`}
                      className="table-cell-link"
                      title={`View ${effectiveCustomerName}`}
                    >
                      {effectiveCustomerName}
                    </NavLink>

                    {customerChanged && (
                      <span
                        title="The order will be reassigned to this customer on save"
                        style={{
                          fontSize: 11,
                          fontWeight: 600,
                          color: '#92400e',
                          background: '#fef3c7',
                          borderRadius: 4,
                          padding: '1px 6px',
                        }}
                      >
                        switched
                      </span>
                    )}

                    {canEditAttributes && customerOptions.length > 0 && (
                      <button
                        type="button"
                        className="btn btn--link"
                        onClick={() => setShowCustomerSwitch(true)}
                      >
                        Switch
                      </button>
                    )}
                  </>
                )}

                {!customerChanged && item.order?.customer?.tier && (
                  <span className="manage-dialog__tier">
                    <span
                      className="manage-dialog__tier-badge"
                      style={{
                        borderColor: normalizeTierBadgeColor(item.order.customer.tier.badgeColor),
                        color: normalizeTierBadgeColor(item.order.customer.tier.badgeColor),
                        backgroundColor: `${normalizeTierBadgeColor(
                          item.order.customer.tier.badgeColor
                        )}14`,
                      }}
                      title="Customer tier"
                    >
                      {item.order.customer.tier.name}
                    </span>

                    <span className="manage-dialog__tier-discount">
                      {formatTierDiscount(item.order.customer.tier.discountAmountCents)} discount
                    </span>
                  </span>
                )}
              </div>
            )}

            {customerChanged && (
              <div className="info-banner info-banner--warning" style={{ marginTop: 8 }}>
                <strong>Switching customer to {effectiveCustomerName}.</strong>{' '}
                {orderLoadCount > 1
                  ? `This reassigns the whole order — all ${orderLoadCount} loads move to this customer. `
                  : ''}
                {!form.deliveryAddressId || productMissing
                  ? `The previous Ship To and product belonged to the old customer and have been cleared — choose a delivery location${
                      productMissing ? ' and product' : ''
                    } for ${effectiveCustomerName} below (we've scrolled to the Ship To). Region, pricing, fees, tier, fulfilment method and enabled products all follow the new Ship To and apply when you Update.`
                  : `Review the Ship To and details below, then click Update to save.`}
              </div>
            )}

            {/* Old customer's Blume figures — hidden once a switch is staged. */}
            {!customerChanged && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 16,
                  flexWrap: 'wrap',
                  marginTop: 4,
                }}
              >
                <span
                  className="muted tiny"
                  style={{
                    display: 'inline-flex',
                    gap: 4,
                    alignItems: 'center',
                    fontSize: '12px',
                  }}
                >
                  Credit Limit:
                  <strong style={{ color: '#172033' }}>
                    {formatBlumeMoney(item.order?.customer?.blumeCreditLimit)}
                  </strong>
                </span>
                <span
                  className="muted tiny"
                  style={{ display: 'inline-flex', gap: 4, alignItems: 'center', fontSize: '12px' }}
                >
                  Available Credit:
                  <strong
                    style={{
                      color: '#172033',
                    }}
                  >
                    {formatBlumeMoney(item.order?.customer?.blumeCreditAvailable)}
                  </strong>
                </span>

                <span
                  className="muted tiny"
                  style={{
                    display: 'inline-flex',
                    gap: 4,
                    alignItems: 'center',
                    fontSize: '12px',
                  }}
                >
                  Overdue Balance:
                  <strong
                    style={{
                      color:
                        Number(item.order?.customer?.blumeOverdueInvoiceBalance ?? 0) > 0
                          ? '#b42318'
                          : '#172033',
                    }}
                  >
                    {formatBlumeMoney(item.order?.customer?.blumeOverdueInvoiceBalance)}
                  </strong>
                </span>
              </div>
            )}

            <div className="muted tiny">
              Created:{' '}
              {persistedOrderCreatedAt
                ? new Date(persistedOrderCreatedAt).toLocaleDateString()
                : '—'}
            </div>

            {selectedProductNotEnabledAtShipTo && !showProductManager && (
              <div className="info-banner info-banner--warning" style={{ marginTop: 10 }}>
                The selected product is not enabled at the current Ship To location. Open
                <strong> Manage products</strong> to enable it here or select another product.
              </div>
            )}

            {showProductManager &&
              isApprovedAlready &&
              canEditAttributes &&
              item.order?.customerId &&
              productAccessAddressId && (
                <ProductAccessManager
                  groups={managerGroups}
                  selectedProductId={form.selectedProductId}
                  onPick={handlePickProduct}
                  locationLabel={productLocationLabel(selectedDeliveryAddress)}
                  search={productSearch}
                  onSearchChange={setProductSearch}
                  hasActiveSearch={Boolean(debouncedProductSearch)}
                  fetching={productManagerFetching}
                  disabled={enablingProduct}
                />
              )}
          </div>
          <div className="manage-dialog__header-actions">
            <div className="manage-dialog__navigation">
              <button
                type="button"
                className="btn btn--ghost btn--sm"
                onClick={() => handleNavigate('previous')}
                disabled={!hasPrevious || isSaving || isRejecting || isNavigating}
                title="Previous order"
                aria-label="Previous order"
              >
                <ChevronLeft size={15} />
                Previous
              </button>

              {currentPosition != null && totalItems != null && (
                <span className="muted tiny manage-dialog__position">
                  {currentPosition} of {totalItems}
                </span>
              )}

              <button
                type="button"
                className="btn btn--ghost btn--sm"
                onClick={() => handleNavigate('next')}
                disabled={!hasNext || isSaving || isRejecting || isNavigating}
                title="Next order"
                aria-label="Next order"
              >
                Next
                <ChevronRight size={15} />
              </button>
            </div>

            <button
              type="button"
              className="btn btn--ghost"
              onClick={handleClose}
              disabled={isSaving}
            >
              Close
            </button>
          </div>
        </div>

        <div className="modal__body">
          {/* Status bar */}
          <div className="status-line status-line--editable">
            <div className="load-header">
              {/* Row 1 — the load's state. Fixed height, so the email stamp
                  appearing or its timestamp growing never moves anything. */}
              <div className="load-header__state">
                <div className="status-ctrl">
                  <span className="muted tiny">Status:</span>

                  <select
                    className={`status-select status-select--${(
                      form.nextStatus || 'unknown'
                    ).toLowerCase()}`}
                    value={form.nextStatus}
                    onChange={(e) => handleSetStatus(e.target.value)}
                    disabled={!canChangeStatus}
                  >
                    {STATUS_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="load-header__email">
                  <Mail size={12} className="load-header__email-icon" />

                  <span className="muted tiny">
                    {pickupEmailedAt
                      ? `Pickup instructions emailed ${formatEmailedAt(pickupEmailedAt)}`
                      : 'Pickup instructions not emailed'}
                  </span>

                  {status === 'READY_FOR_PICKUP' && !pickupEmailedAt && (
                    <button
                      type="button"
                      className="btn btn--link"
                      onClick={() => setShowPickupRecipients(true)}
                      disabled={!canNotifyPickupCustomer || notifyPickupCustomer.isPending}
                      title={
                        hasUnsavedChanges
                          ? 'Save your changes before notifying the customer.'
                          : 'Email pickup instructions to the customer.'
                      }
                    >
                      {notifyPickupCustomer.isPending ? 'Sending…' : 'Notify customer'}
                    </button>
                  )}

                  {loadNotifications.length > 0 && (
                    <button
                      type="button"
                      className="btn btn--link"
                      onClick={() => setShowEmailHistory(true)}
                    >
                      Email history ({loadNotifications.length})
                    </button>
                  )}
                </div>
              </div>

              {/* Row 2 — the two fields that belong to the ORDER, not this
                  load. Kept together and away from the item form below. */}
              <div className="load-header__order-fields">
                <div className="form-control">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <CopyButton value={form.poNumber} label="PO number" />
                    <label>
                      PO Number{commonSaveGateContext.requiresPoNumber ? ' *' : ' (optional)'}
                    </label>
                  </div>

                  <input
                    className="select-lg"
                    type="text"
                    value={form.poNumber}
                    onChange={(e) =>
                      dispatch({
                        type: 'SET_PO_NUMBER',
                        value: e.target.value,
                      })
                    }
                    disabled={!canEditAttributes}
                    style={
                      commonSaveGateContext.requiresPoNumber && !form.poNumber.trim()
                        ? { borderColor: '#dc2626' }
                        : undefined
                    }
                  />
                  {commonSaveGateContext.requiresPoNumber && !form.poNumber.trim() && (
                    <div className="error-text" style={{ marginTop: 4 }}>
                      This sub-location requires a PO number.
                    </div>
                  )}
                </div>

                <div className="form-control">
                  <label>Tags</label>

                  <OrderTagsEditor
                    tags={form.tags}
                    savedTags={savedOrderTags}
                    savedTagsLoading={savedOrderTagsPending}
                    savingTag={saveOrderTag.isPending}
                    deletingTag={deleteSavedOrderTag.isPending}
                    disabled={!canEditAttributes}
                    onChange={(tags) =>
                      dispatch({
                        type: 'SET_TAGS',
                        value: tags,
                      })
                    }
                    onSaveTag={async (name: string) => {
                      try {
                        await saveOrderTag.mutateAsync(name);

                        toast.success(`Saved tag "${name}".`);
                      } catch (error: any) {
                        toast.error(error?.response?.data?.error || 'Failed to save tag.');

                        throw error;
                      }
                    }}
                    onDeleteSavedTag={async (tagId: string) => {
                      try {
                        await deleteSavedOrderTag.mutateAsync(tagId);

                        toast.success('Saved tag removed.');
                      } catch (error: any) {
                        toast.error(error?.response?.data?.error || 'Failed to remove saved tag.');

                        throw error;
                      }
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

          {isProductDeleted && (
            <div className="error-box error-box--spaced">
              This item's product has been deleted. You cannot approve or allocate stock for this
              item.
            </div>
          )}

          {/* Two-column layout */}
          <div className="manage-grid">
            {/* LEFT */}
            <div className="section">
              <AddressSection
                form={form}
                dispatch={dispatch}
                item={item}
                locationOptions={locationOptions}
                overrideLocationOptions={overrideLocationOptions}
                selectedLocation={selectedLocation}
                deliveryAddresses={deliveryAddresses}
                selectedDeliveryAddress={selectedDeliveryAddress}
                canEditAttributes={canEditAttributes}
                organizationDefaultLoadOption={organizationDefaultLoadOption}
                shipToRef={shipToRef}
              />
              <CarrierSection
                form={form}
                dispatch={dispatch}
                item={item}
                allCarriers={allCarriers}
                fullCarrierProfile={fullCarrierProfile}
                canAssignCarrier={canAssignCarrier}
                carrierAssignmentAdvancesStatus={carrierAssignmentAdvancesStatus}
                canEditAttributes={canEditAttributes}
                hasBOLUnsaved={hasBOLUnsaved}
                onDownloadBOL={handleDownloadBOL}
                onOpenSendBOL={handleSendBOL}
                onPreviewBOL={handlePreviewBOL}
                generateBOLPending={generateBOL.isPending}
                previewBOLPending={previewBOL.isPending}
                removeCarrierPending={removeCarrierMutation.isPending}
              />
            </div>

            {/* RIGHT */}
            <FulfillmentSection
              form={form}
              dispatch={dispatch}
              item={item}
              palletSizes={palletSizes}
              isPallet={isPallet}
              isWaitlist={isWaitlist}
              hidePrice={hidePrice}
              isApprovedAlready={isApprovedAlready}
              canEdit={canEdit}
              canEditAttributes={canEditAttributes}
              availableLoadOptions={addressLoadOptions}
              loadOptionsReady={loadOptionsReady}
              defaultFulfillmentMethodId={effectiveDefaultFulfillmentMethodId}
              usingOrganizationDefault={usingOrganizationDefault}
              organizationLoadOptions={organizationLoadOptions}
              hasLocationSpecificLoadOptions={hasLocationSpecificLoadOptions}
              onEnableLocationLoadOption={handleEnableLocationLoadOption}
              onDisableLocationLoadOption={handleDisableLocationLoadOption}
              locationLoadOptionChangePending={
                enableCustomerLocationLoadOption.isPending ||
                disableCustomerLocationLoadOption.isPending
              }
              configuredCarrierRateCents={resolveCarrierRateCents(
                selectedDeliveryAddress,
                form.locationId
              )}
            />
          </div>

          <div className="form-control form-control--note">
            <label>Internal Notes</label>
            <textarea
              className="select-lg"
              value={form.internalNotes}
              onChange={(e) => dispatch({ type: 'SET_INTERNAL_NOTES', value: e.target.value })}
              disabled={!canEditAttributes}
              placeholder="Internal notes (not visible to customer)"
            />
          </div>

          <DeliveryTicketCard
            orderId={item.order.id}
            orderItemId={item.id}
            loadNumber={item.loadNumber ?? null}
            status={status}
            current={{
              quantity: Number(item.quantity) || 0,
              ticketNumber: item.ticketNumber ?? '',
              poNumber: item.order?.poNumber ?? '',
              deliveryDate: item.deliveryDate
                ? new Date(item.deliveryDate).toISOString().slice(0, 10)
                : '',
            }}
            canEdit={canEdit}
            onApply={(draft) => {
              const tons = Number(draft.quantity);
              if (Number.isFinite(tons) && tons > 0)
                dispatch({ type: 'SET_QUANTITY', value: tons });
              dispatch({ type: 'SET_TICKET_NUMBER', value: draft.ticketNumber });
              if (draft.poNumber) dispatch({ type: 'SET_PO_NUMBER', value: draft.poNumber });
              if (draft.deliveryDate)
                dispatch({ type: 'SET_DELIVERY_DATE', value: draft.deliveryDate });
              // A reviewed ticket means delivered: stage DELIVERED (visible in the
              // status dropdown) so the normal Update saves it, or they can revert.
              if (status !== 'DELIVERED') {
                dispatch({ type: 'SET_STATUS', value: 'DELIVERED' });
                toast.info('Ticket applied — status set to Delivered. Click Update to save.');
              }
            }}
          />

          <OrderDocumentsPanel orderId={item.order.id} canManage={canEdit} />

          <OrderAuditLogPanel orderId={item.order.id} orderItemId={item.id} />

          <div className="manage-notes-section">
            <OrderNotesPanel orderId={item.order.id} />
          </div>
        </div>

        {/* Footer */}
        <div
          className={`modal__footer manage-footer${footerCollapsed ? ' manage-footer--collapsed' : ''}`}
        >
          <button
            type="button"
            className="manage-footer__toggle"
            onClick={() => setFooterCollapsed((v) => !v)}
            aria-expanded={!footerCollapsed}
          >
            {footerCollapsed ? 'Show actions' : 'Hide actions'}
          </button>
          <div>
            {canEdit && !['REJECTED', 'CANCELLED', 'DELIVERED', 'SHIPPED'].includes(status) && (
              <button
                className="btn btn--danger"
                onClick={() => {
                  setRejectReason('');
                  setShowReject(true);
                }}
                disabled={isRejecting}
              >
                Reject
              </button>
            )}
          </div>
          <div className="actions manage-footer-actions">
            <div className="muted tiny">
              Changes will update stock reservations and order totals.
            </div>

            <button
              type="button"
              className="btn btn--secondary"
              onClick={() => setIsInvoiceDialogOpen(true)}
              disabled={hasUnsavedChanges || isSaving || isRejecting}
              title={
                hasUnsavedChanges
                  ? 'Save changes before preparing the QuickBooks invoice.'
                  : 'Review the saved BMP values before creating a QuickBooks invoice.'
              }
            >
              <ReceiptText size={15} />
              Invoice
            </button>

            <button
              className="btn btn--primary"
              onClick={() => setIsReplicateDialogOpen(true)}
              disabled={approveDisabled && form.nextStatus === status}
            >
              Replicate
            </button>
            {isPrepayRequiredStatus ? (
              <>
                <button
                  className="btn btn--primary"
                  onClick={() => handleSave('update')}
                  disabled={primaryActionDisabled}
                  title={primaryActionDisabled ? saveDisabledTitle : ''}
                >
                  {isSaving && saveIntent === 'update' ? 'Updating…' : 'Update'}
                </button>

                <button
                  className="btn btn--primary"
                  onClick={() => handleSave('approve')}
                  disabled={prepayApproveDisabled}
                  title={prepayApproveDisabled ? prepayApproveDisabledTitle : ''}
                >
                  {isSaving && saveIntent === 'approve' ? 'Approving…' : 'Approve'}
                </button>
              </>
            ) : (
              <button
                className="btn btn--primary"
                onClick={() => handleSave()}
                disabled={primaryActionDisabled}
                title={primaryActionDisabled ? saveDisabledTitle : ''}
              >
                {isSaving
                  ? usesApproveAction
                    ? 'Approving…'
                    : 'Updating…'
                  : usesApproveAction
                    ? 'Approve'
                    : 'Update'}
              </button>
            )}
          </div>
        </div>

        {/* Modals */}
        {showReject && (
          <RejectReasonDialog
            reason={rejectReason}
            onChangeReason={setRejectReason}
            onCancel={() => setShowReject(false)}
            onConfirm={async () => {
              await onReject({
                orderId: item.order.id,
                itemId: item.id,
                reason: rejectReason?.trim() || undefined,
              });
              setShowReject(false);
            }}
            isSubmitting={isRejecting}
          />
        )}

        {showEmailHistory && (
          <EmailHistoryDialog
            itemId={item.id}
            loadNumber={item.loadNumber}
            onClose={() => setShowEmailHistory(false)}
          />
        )}

        {showPickupRecipients && pickupOrderId && (
          <PickupRecipientsDialog
            orderId={pickupOrderId}
            itemId={item.id}
            onCancel={() => setShowPickupRecipients(false)}
            onConfirm={handleNotifyPickupCustomer}
            isSubmitting={notifyPickupCustomer.isPending}
          />
        )}

        {showBOLModal && (
          <SendBOLDialog
            item={item}
            carrier={form.localCarrier}
            contacts={fullCarrierProfile?.contacts ?? []}
            assignedContactId={form.driverContactId || null}
            hasUnsavedChanges={hasBOLUnsaved}
            onCancel={() => setShowBOLModal(false)}
            onConfirm={handleSendBOLConfirm}
            isSubmitting={sendBOLMutation.isPending}
          />
        )}

        {previewUrl && (
          <BOLPreviewDialog
            url={previewUrl}
            loadNumber={item.loadNumber}
            hasUnsavedChanges={hasBOLUnsaved}
            onClose={closePreview}
            onDownload={() => {
              handleDownloadBOL();
              closePreview();
            }}
            onSend={() => {
              closePreview();
              setShowBOLModal(true);
            }}
          />
        )}

        <InvoiceDialog
          isOpen={isInvoiceDialogOpen}
          orderIds={[item.order.id]}
          onClose={() => setIsInvoiceDialogOpen(false)}
          onInvoiceCreated={(_orderId, result) => {
            // Only adopt a persisted status once QBO returned an invoice ID.
            if (!result.invoice.quickBooksInvoiceId) return;

            // The visible status <select> uses form.nextStatus, not persistedStatus.
            // Update both and make the invoice transition the new clean baseline.
            const nextStatus = status === 'PAID' ? 'PAID' : 'INVOICED';
            setPersistedStatus(nextStatus);
            rebaseline({
              ...form,
              nextStatus,
              pendingCarrierId: undefined,
            });
          }}
        />

        <ReplicateOrderDialog
          isOpen={isReplicateDialogOpen}
          onClose={() => setIsReplicateDialogOpen(false)}
          orderId={item.order.id}
          sourceItemId={item.id}
          sourceLoadNumber={item.loadNumber || `#${item.order?.id?.slice(-6) || ''}`}
          customerId={item.cust?.id ?? ''}
          customerName={item.cust?.name ?? 'Customer'}
          hasCarrier={Boolean(item.carrierId)}
          defaults={{
            productId: item.productId ?? item.product?.id ?? '',
            deliveryAddressId: item.order?.deliveryAddress?.id ?? '',
            orgLoadOptionId: item.orgLoadOptionIdSnapshot ?? '',
            quantity: Number(item.quantity) || 0,
          }}
          onReplicatedItem={(newItemId) => onReplicatedItem?.({ id: newItemId })}
        />
      </div>
    </div>
  );
}
